// ──────────────────────────────────────────────────────────────
// PART 1/3 – Imports, State, Base Data & Filtering Logic
// ──────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { getCoords } from './geoHelper';
import DeliveryManifest from './DeliveryManifest';
import { normalizeAddress } from './addressNormalizer';
import { getOrderAddresses } from './DeliveryManifest';
// Disable React DevTools + WebSocket in production & staging
if (process.env.NODE_ENV === 'production') {
  window.__REACT_DEVTOOLS_GLOBAL_HOOK__ = {};
}

// Suppress the annoying WebSocket error spam
const originalError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('WebSocket connection to')) {
    return; // ignore WebSocket spam
  }
  originalError(...args);
};
function AdminPanel() {
   const baseURL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5000' 
  : '';
    // FORCE the staging URL if we aren't on localhost
// const baseURL = window.location.hostname === 'localhost' 
//   ? 'http://localhost:5000' 
//   : 'https://paradise-backend-staging.onrender.com'; // Hardcode your backend here


  // Smart base URL: localhost → dev, render → prod
//   const baseURL = window.location.hostname === 'localhost' 
//     ? 'http://localhost:5000' 
//     : '';

  const adminPath = process.env.REACT_APP_ADMIN_PATH || 'admin';

  const [isAdmin, setIsAdmin] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [orders, setOrders] = useState([]);

  const [filterDate, setFilterDate] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [searchName, setSearchName] = useState('');
const [itemSearch, setItemSearch] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // all, pickup, delivery, cancelled
  const [viewMode, setViewMode] = useState('cards');
  const [sortMode, setSortMode] = useState('latest');
const [refreshKey, setRefreshKey] = useState(0); // increment to force re-render
  const [summaryPeriod, setSummaryPeriod] = useState('day'); // 'day', 'month', 'year'
  const [totalsViewMode, setTotalsViewMode] = useState('cards'); // 'cards' or 'table'
  // Add this line near your other useState definitions (usually near the top of the file)
const [kitchenDisplayMode, setKitchenDisplayMode] = useState('cards');

const [optimizedDeliveries, setOptimizedDeliveries] = useState([]);
// AdminPanel.js
const [optimizedStops, setOptimizedStops] = useState([]);
const [optimizedRoute, setOptimizedRoute] = useState([]); // This is the blue line for the map

const [isAlphabetical, setIsAlphabetical] = useState(false);

  const [showSales, setShowSales] = useState(true); // true = sales, false = kitchen

  // ── Derived filtered sets ─────────────────────────────────────
  const activeOrders   = orders.filter(o => o.status !== 'cancelled');
  const archivedOrders = orders.filter(o => o.status === 'cancelled');
  const activePickups  = activeOrders.filter(o => !o.delivery);
  const activeDeliveries = activeOrders.filter(o => o.delivery);

  // Main filtered list depending on current tab
  let baseSet = activeOrders;
  if (activeTab === 'cancelled') baseSet = archivedOrders;
const filteredOrders = baseSet.filter(o => {
  // 1. Name Search (Using matchesSearch to avoid errors)
  const matchesSearch = !searchName || 
    (o.name && o.name.toLowerCase().includes(searchName.toLowerCase()));

  // 2. Phone Search (Cleaned for dashes/brackets)
  const cleanSearchPhone = phoneSearch ? phoneSearch.replace(/\D/g, '') : '';
  const cleanOrderPhone = (o.phone || '').replace(/\D/g, '');
  const matchesPhone = !phoneSearch || cleanOrderPhone.includes(cleanSearchPhone);

  // 3. Date & Type
  const matchesDate = !filterDate || o.date === filterDate;
  const matchesType = filterType === 'All' ||
    (filterType === 'Pickup' && !o.delivery) ||
    (filterType === 'Delivery' && o.delivery);

  // 4. Item/Menu Search (The Owner's Product Filter)
  const s = (itemSearch || '').toLowerCase();
  const matchesItem = !s || (
    // Check if searching for Menu1 or Menu2
    (s.includes('menu1') && Number(o.menu1Persons || 0) > 0) ||
    (s.includes('menu2') && Number(o.menu2Persons || 0) > 0) ||
    // Check if searching for an A-La-Carte item name
    Object.keys(o.alaCarte || {}).some(key => key.toLowerCase().includes(s))
  );

  // THE FIX: Returning all conditions combined
  return matchesSearch && matchesPhone && matchesDate && matchesType && matchesItem;
});
  // Sorted display list
  let displayList = [...filteredOrders];
  if (sortMode === 'alpha') {
    displayList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  } else {
    displayList.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }
const getFilteredSummaryOrders = () => {
  const now = new Date();
  let filtered = activeOrders; // Use activeOrders to exclude cancelled orders

  // Helper to normalize date to YYYY-MM-DD
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return date.toISOString().split('T')[0];
    } catch {
      return null;
    }
  };

  if (summaryPeriod === 'day') {
    // Use selected filterDate or today
    const targetDate = normalizeDate(filterDate) || now.toISOString().split('T')[0];
    filtered = filtered.filter(o => {
      const orderDate = normalizeDate(o.date);
      return orderDate === targetDate;
    });
    // Optional debug
    // console.log('Day filter applied - Target:', targetDate, 'Results:', filtered.length);
  } 
  else if (summaryPeriod === 'month') {
    // Use selected month or current month
    let targetMonth = now.toISOString().slice(0, 7); // YYYY-MM
    if (filterDate) {
      const selectedDate = normalizeDate(filterDate);
      if (selectedDate) {
        targetMonth = selectedDate.slice(0, 7);
      }
    }
    filtered = filtered.filter(o => {
      const orderDate = normalizeDate(o.date);
      return orderDate?.startsWith(targetMonth);
    });
    // Optional debug
    // console.log('Month filter applied - Target:', targetMonth, 'Results:', filtered.length);
  } 
  else if (summaryPeriod === 'year') {
    // Use selected year or current year
    let targetYear = now.toISOString().slice(0, 4); // YYYY
    if (filterDate) {
      const selectedDate = normalizeDate(filterDate);
      if (selectedDate) {
        targetYear = selectedDate.slice(0, 4);
      }
    }
    filtered = filtered.filter(o => {
      const orderDate = normalizeDate(o.date);
      return orderDate?.startsWith(targetYear);
    });
    // Optional debug
    // console.log('Year filter applied - Target:', targetYear, 'Results:', filtered.length);
  }

  return filtered;
};

  // ── Menu & Price Data ─────────────────────────────────────────
  const menu1Items = [
    "CHOPPED LIVER WITH FRIED ONIONS", "CHICKEN SOUP WITH MATZA BALLS",
    "GEFILTE FISH", "COLESLAW SALAD", "ROMANIAN EGGPLANT", "POTATO KUGGEL", "BBQ CHICKEN",
    "BEEF BRISKET WITH GRAVY", "STIR FRY VEGGIES", "GARLIC BAKED POTATOES", "CHOCOLATE ROLL CAKE"
  ];

  const menu2Items = [
    "FISHBALLS IN TOMATO SAUCE", "VEGETABLE SOUP", "SALADE CUITE", "MORROCAN EGGPLANT", "COOKED CARROTS",
    "LEMON CHICKEN WITH OLIVES", "BEEF SHOULDER ROAST WITH GRAVY", "STIR FRY VEGGIES",
    "GARLIC BAKED POTATOES", "CHOCOLATE ROLL CAKE"
  ];

  const itemPrices = {
    "LEAN BEEF BRISKET WITH GRAVY": { price: 65, unit: "lb" },
    "PEPPER STEAK": { price: 40, unit: "lb" },
    "GRILLED CHICKEN BREAST": { price: 34, unit: "lb" },
    "CHICKEN SCHNITZEL": { price: 32, unit: "lb" },
    "CHICKEN PARGIOT": { price: 34, unit: "lb" },
    "GENERAL TAO CHICKEN": { price: 32, unit: "lb" },
    "PINEAPPLE CHICKEN": { price: 32, unit: "lb" },
    "CRISPY BEEF": { price: 42, unit: "lb" },
    "BEEF CABBAGE ROLLS": { price: 22, unit: "lb" },
    "SWEET 'N' SOUR MEATBALLS": { price: 28, unit: "lb" },
    "BBQ CHICKEN": { price: 25, unit: "ea" },
    "TERIYAKI BBQ CHICKEN": { price: 30, unit: "ea" },
    "SHEPHERD'S PIE": { price: 24, unit: "ea" },
    "HOMEMADE GEFILTE FISH": { price: 18, unit: "ea" },
    "BEEF PASTEL": { price: 5.25, unit: "ea" },
    "MOROCCAN COOKED SALMON": { price: 34, unit: "lb" },
    "TERIYAKI SALMON": { price: 36, unit: "lb" },
    "GRILLED LEMON SALMON": { price: 36, unit: "lb" },
    "GARLIC BAKED POTATOES": { price: 14, unit: "lb" },
    "STIR FRY VEGETABLES": { price: 18, unit: "lb" },
    "MATZA BALL SOUP (4 BALLS)": { price: 20, unit: "ea" },
    "MATZA BALLS NO SOUP (4 BALLS PER CONTAINER)": { price: 6, unit: "cont" },
    "CLEAR CHICKEN SOUP": { price: 14, unit: "ea" },
    "VEGETABLE SOUP": { price: 14, unit: "ea" },
    "FANCY SEDER PLATES": { price: 36, unit: "ea", taxable: true },
    "CHOPPED LIVER W/FRIED ONIONS": { price: 24, unit: "lb" },
    "ROMANIAN EGGPLANT SALAD": { price: 18, unit: "lb" },
    "ISRAELI EGGPLANT SALAD": { price: 18, unit: "lb" },
    "SALADE CUITE": { price: 18, unit: "lb" },
    "BABAGANOUSH SALAD": { price: 18, unit: 'lb', isGF: false },
    "CUCUMBER SALAD": { price: 10, unit: 'lb', isGF: true },
    "POTATO KUGEL": { price: 15, unit: 'ea', isGF: false },
    "SWEET KUGEL": { price: 15, unit: 'ea', isGF: false }
  };

  // End of Part 1/3 ───────────────────────────────────────────────
  // ──────────────────────────────────────────────────────────────
// PART 2/3 – Helper Functions, Kitchen Logic, Order Calc, CRUD
// ──────────────────────────────────────────────────────────────

// Portion calculation
const calculatePortion = (ppl, itemName) => {
  if (!ppl || ppl <= 0) return null;
  const name = itemName.toUpperCase().trim();

  if (["CHOPPED LIVER", "ROMANIAN EGGPLANT", "SALADE CUITE", "MORROCAN EGGPLANT", "COOKED CARROTS"].some(s => name.includes(s))) {
    if (ppl <= 6) return "1 LBS"; if (ppl <= 12) return "2 LBS"; if (ppl <= 18) return "3 LBS"; if (ppl <= 24) return "4 LBS";
    return Math.ceil(ppl / 6) + " LBS";
  }
  if (["CHICKEN SOUP", "VEGETABLE SOUP"].some(s => name.includes(s))) {
    if (ppl <= 5) return "2 CONTAINERS"; if (ppl <= 7) return "3 CONTAINERS"; if (ppl <= 9) return "4 CONTAINERS";
    if (ppl <= 11) return "5 CONTAINERS"; if (ppl <= 13) return "6 CONTAINERS"; if (ppl <= 15) return "7 CONTAINERS";
    if (ppl <= 17) return "8 CONTAINERS"; if (ppl <= 19) return "9 CONTAINERS"; if (ppl <= 21) return "10 CONTAINERS";
    return Math.ceil(ppl / 2) + " CONTAINERS";
  }
  if (["GEFILTE FISH", "CHOCOLATE ROLL CAKE", "POTATO KUGGEL"].some(s => name.includes(s))) {
    if (ppl <= 6) return "1"; if (ppl <= 12) return "2"; if (ppl <= 18) return "3"; if (ppl <= 24) return "4";
    return Math.ceil(ppl / 6) + "";
  }
  if (name.includes("COLESLAW")) {
    if (ppl <= 5) return "1 LBS"; if (ppl <= 7) return "1 ½ LBS"; if (ppl <= 11) return "2 LBS";
    if (ppl <= 15) return "3 LBS"; if (ppl <= 18) return "4 LBS"; if (ppl <= 22) return "5 LBS";
    return Math.ceil(ppl / 4.4) + " LBS";
  }
  if (["BBQ CHICKEN", "LEMON CHICKEN WITH OLIVES"].some(s => name.includes(s))) {
    if (ppl <= 4) return "1"; if (ppl <= 8) return "2"; if (ppl <= 12) return "3"; if (ppl <= 16) return "4";
    if (ppl <= 21) return "5"; if (ppl <= 25) return "6"; return Math.ceil(ppl / 4) + "";
  }
  if (["BEEF BRISKET WITH GRAVY", "STIR FRY VEGGIES", "GARLIC BAKED POTATOES", "BEEF SHOULDER ROAST WITH GRAVY"].some(s => name.includes(s))) {
    if (ppl <= 4) return "1 LBS"; if (ppl <= 7) return "1 ½ LBS"; if (ppl <= 9) return "2 LBS"; if (ppl <= 11) return "2 ½ LBS";
    if (ppl <= 15) return "3 LBS"; if (ppl <= 17) return "3 ½ LBS"; if (ppl <= 20) return "4 LBS"; if (ppl <= 23) return "4 ½ LBS"; if (ppl <= 26) return "5 LBS";
    return Math.ceil(ppl / 5.2) + " LBS";
  }
  if (name.includes("FISHBALLS")) return (ppl * 2) + " PCS";
  return null;
};

// Kitchen helpers
const categoryMap = {
  'CHOPPED LIVER': 'Salad',
  'ROMANIAN EGGPLANT': 'Salad',
  'SALADE CUITE': 'Salad',
  'MORROCAN EGGPLANT': 'Salad',
  'COOKED CARROTS': 'Salad',
  'COLESLAW SALAD': 'Salad',
  'ISRAELI EGGPLANT SALAD': 'Salad',
  'CHOPPED LIVER W/FRIED ONIONS': 'Salad',
  'ROMANIAN EGGPLANT SALAD': 'Salad',
  'CHICKEN SOUP': 'Soups',
  'VEGETABLE SOUP': 'Soups',
  'CLEAR CHICKEN SOUP': 'Soups',
  'MATZA BALL SOUP (4 BALLS)': 'Soups',
  'MATZA BALLS NO SOUP (4 BALLS PER CONTAINER)': 'Soups',
  'GEFILTE FISH': 'Fish',
  'FISHBALLS IN TOMATO SAUCE': 'Fish',
  'HOMEMADE GEFILTE FISH': 'Fish',
  'MOROCCAN COOKED SALMON': 'Fish',
  'TERIYAKI SALMON': 'Fish',
  'GRILLED LEMON SALMON': 'Fish',
  'BBQ CHICKEN': 'Chicken',
  'LEMON CHICKEN WITH OLIVES': 'Chicken',
  'GRILLED CHICKEN BREAST': 'Chicken',
  'CHICKEN SCHNITZEL': 'Chicken',
  'CHICKEN PARGIOT': 'Chicken',
  'GENERAL TAO CHICKEN': 'Chicken',
  'PINEAPPLE CHICKEN': 'Chicken',
  'TERIYAKI BBQ CHICKEN': 'Chicken',
  'BEEF BRISKET WITH GRAVY': 'Beef',
  'BEEF SHOULDER ROAST WITH GRAVY': 'Beef',
  'LEAN BEEF BRISKET WITH GRAVY': 'Beef',
  'PEPPER STEAK': 'Beef',
  'CRISPY BEEF': 'Beef',
  'BEEF CABBAGE ROLLS': 'Beef',
  "SWEET 'N' SOUR MEATBALLS": 'Beef',
  "SHEPHERD'S PIE": 'Sides',
  'BEEF PASTEL': 'Sides',
  'POTATO KUGGEL': 'Sides',
  'STIR FRY VEGGIES': 'Sides',
  'GARLIC BAKED POTATOES': 'Sides',
  'STIR FRY VEGETABLES': 'Sides',
  'CHOCOLATE ROLL CAKE': 'Desserts',
  'FANCY SEDER PLATES': 'Other',
};

const unitMap = {
  'PCS': 'M',
  'DZ': 'dz',
  'BALLS': 'balls',
  'LB': 'lb',
  'LBS': 'lb',
  'EA': 'ea',
  'CONTAINERS': 'cont',
  'CONT': 'cont',
};

const kitchenNameNormalization = {
  'LEAN BEEF BRISKET WITH GRAVY': 'BEEF BRISKET WITH GRAVY',
  'BEEF BRISKET': 'BEEF BRISKET WITH GRAVY',
  'CHOPPED LIVER': 'CHOPPED LIVER W/FRIED ONIONS',
  'CHOPPED LIVER WITH FRIED ONIONS': 'CHOPPED LIVER W/FRIED ONIONS',
  'STIR FRY VEGETABLES': 'STIR FRY VEGGIES',
  'ROMANIAN EGG PLANT': 'ROMANIAN EGGPLANT',
  'ROMANIAN EGGPLANT SALAD': 'ROMANIAN EGGPLANT',
  'ROMANIAN EGGPLANT': 'ROMANIAN EGGPLANT',
  'MATZA BALLS NO SOUP (4 BALLS PER CONTAINER)': 'MATZA BALLS NO SOUP',
  'MATZA BALL SOUP (4 BALLS)': 'CHICKEN SOUP WITH MATZA BALLS',
  'MATZA BALLS': 'MATZA BALLS NO SOUP',
  'CHOCOLATE ROLL CAKE': 'CHOCOLATE ROLL CAKE',
  'GEFILTE FISH': 'HOMEMADE GEFILTE FISH',
  'POTATO KUGEL': 'POTATO KUGGEL',
  'SWEET KUGEL': 'POTATO KUGGEL',
  'SWEET POTATO KUGEL': 'POTATO KUGGEL',
  'COLESLAW': 'COLESLAW SALAD',
  'COOKED CARROTS': 'COOKED CARROTS SALAD',
  'FISHBALLS IN TOMATO SAUCE': 'FISHBALLS',
};

function mapUnit(u) {
  return unitMap[u.toUpperCase()] || u;
}

const getDisplayUnit = (agg) => {
  const upperUnit = agg.unit.toUpperCase();
  const dualUnitItems = [
    'BBQ CHICKEN', 'LEMON CHICKEN WITH OLIVES', 'GRILLED CHICKEN BREAST',
    'CHICKEN SCHNITZEL', 'CHICKEN PARGIOT', 'GENERAL TAO CHICKEN',
    'PINEAPPLE CHICKEN', 'TERIYAKI BBQ CHICKEN',
    'MOROCCAN COOKED SALMON', 'TERIYAKI SALMON', 'GRILLED LEMON SALMON',
    'BEEF BRISKET WITH GRAVY', 'LEAN BEEF BRISKET WITH GRAVY',
    'BEEF SHOULDER ROAST WITH GRAVY', 'PEPPER STEAK', 'CRISPY BEEF'
  ];
  const isLikelyAlacarteDual = dualUnitItems.some(item => agg.itemName.toUpperCase().includes(item));
  if ((upperUnit === 'PCS' || upperUnit === 'EA') && isLikelyAlacarteDual) return 'M';
  return mapUnit(agg.unit);
};

function parseQty(str) {
  if (!str) return { num: 0, unit: '' };
  let cleanedStr = str.replace('½', '.5').replace('¼', '.25').replace('¾', '.75').replace('⅓', '.333').replace('⅔', '.666');
  const match = cleanedStr.match(/^([\d\s\.]+)\s*(.*)$/);
  if (!match) {
    const num = parseFloat(cleanedStr);
    return { num: isNaN(num) ? 0 : num, unit: 'EA' };
  }
  let numStr = match[1].trim();
  let unit = match[2].trim().toUpperCase();
  if (unit === '') unit = 'EA';
  if (unit === 'LBS') unit = 'LB';
  const parts = numStr.split(/\s+/);
  let num = parts.reduce((sum, p) => sum + (parseFloat(p) || 0), 0);
  return { num, unit };
}

// const computeKitchenAggregates = () => {
//   const itemAggregates = new Map();
//   filteredOrders.forEach(o => {
//     const addToAggregate = (rawName, num, unit) => {
//       let cleanName = kitchenNameNormalization[rawName.toUpperCase().trim()] || rawName.toUpperCase().trim();
//       let cleanUnit = unit.toUpperCase().trim();
//       if (cleanName.includes("SOUP") && !cleanName.includes("MATZA BALLS NO SOUP")) cleanUnit = "CONT";

//       if (cleanName === "MATZA BALLS NO SOUP") {
//         const ballsPerUnit = cleanUnit === 'DZ' ? 12 : 4;
//         const mathematicalValue = num * ballsPerUnit;
//         const label = cleanUnit === 'DZ' ? `${num}DZ` : `${num * 4}`;
//         const key = `MATZA BALLS NO SOUP|BALLS`;
//         if (!itemAggregates.has(key)) {
//           itemAggregates.set(key, { itemName: "MATZA BALLS NO SOUP", unit: "BALLS", qtys: [], total: 0, category: 'Soups' });
//         }
//         const agg = itemAggregates.get(key);
//         agg.qtys.push(label);
//         agg.total += mathematicalValue;
//         return;
//       }

//       const key = `${cleanName}|${cleanUnit}`;
//       if (!itemAggregates.has(key)) {
//         itemAggregates.set(key, {
//           itemName: cleanName,
//           unit: cleanUnit,
//           qtys: [],
//           total: 0,
//           category: categoryMap[cleanName] || 'Custom'
//         });
//       }
//       const agg = itemAggregates.get(key);
//       agg.qtys.push(num);
//       agg.total += num;
//     };

//     if (o.menu1Persons > 0) {
//       menu1Items.forEach(item => {
//         const portion = calculatePortion(o.menu1Persons, item);
//         if (portion) {
//           const { num, unit } = parseQty(portion);
//           addToAggregate(item, num, unit);
//         }
//       });
//     }
//     if (o.menu2Persons > 0) {
//       menu2Items.forEach(item => {
//         const portion = calculatePortion(o.menu2Persons, item);
//         if (portion) {
//           const { num, unit } = parseQty(portion);
//           addToAggregate(item, num, unit);
//         }
//       });
//     }
//     Object.entries(o.alaCarte || {}).forEach(([name, { qty, chosenUnit }]) => {
//       let num = parseFloat(qty) || 0;
//       let unit = (chosenUnit || 'EA').toUpperCase();
//       addToAggregate(name, num, unit);
//     });
//   });

//   itemAggregates.forEach(agg => {
//     agg.qtys.sort((a, b) => (typeof b === 'number' ? b : 0) - (typeof a === 'number' ? a : 0));
//   });

//   const categories = {};
//   itemAggregates.forEach(agg => {
//     if (!categories[agg.category]) categories[agg.category] = [];
//     categories[agg.category].push(agg);
//   });

//   return categories;
// };
const computeKitchenAggregates = () => {
  const itemAggregates = new Map();

  // Helper inside the loop to ensure normalization logic is identical for all sources
  const addToAggregate = (rawName, num, unit) => {
    // 1. NORMALIZE NAME
    const upperName = rawName.toUpperCase().trim();
    let cleanName = kitchenNameNormalization[upperName] || upperName;

    // 2. NORMALIZE UNIT
    // 2. NORMALIZE UNIT

// ADD 'PC' TO THIS LIST:

    let cleanUnit = unit.toUpperCase().trim();
    if (cleanUnit === 'LBS') cleanUnit = 'LB';
// Catch any variation of pieces/each and turn it into 'M'
if (['PCS', 'PC', 'PIECE', 'PIECES', 'UNITS', 'UNIT', 'EA'].includes(cleanUnit)) {
  cleanUnit = 'M';
}
    // Safety check for Soup containers
    if (cleanName.includes("SOUP") && !cleanName.includes("MATZA BALLS NO SOUP")) {
      cleanUnit = "CONT";
    }

    // 3. MASTER KEY = NAME ONLY (Prevents duplicate rows)
    const key = cleanName;

    if (!itemAggregates.has(key)) {
      itemAggregates.set(key, {
        itemName: cleanName,
        category: categoryMap[cleanName] || 'Custom',
        // Structure to store multiple units: { 'LB': { total: 0, qtys: [] }, 'M': { ... } }
        unitsData: {} 
      });
    }

    const agg = itemAggregates.get(key);

   // 4. SPECIAL CASE: MATZA BALLS (Handling standard 4-pack, DZ, and RAW BALLS)
if (cleanName === "MATZA BALLS NO SOUP") {
  let mathematicalValue = 0;
  let label = "";

  // Check if this was a "Matza Ball" (Single) or a "Container"
  const isSingleBall = upperName === "MATZA BALL" || upperName === "MATZA BALLS";

  if (isSingleBall) {
    // It's already a ball count (like your 9 balls)
    mathematicalValue = num;
    label = `${num}`;
  } else {
    // It's a container or dozen
    const ballsPerUnit = cleanUnit === 'DZ' ? 12 : 4;
    mathematicalValue = num * ballsPerUnit;
    label = cleanUnit === 'DZ' ? `${num}DZ` : `${num * 4}`;
  }
  
  if (!agg.unitsData['BALLS']) agg.unitsData['BALLS'] = { total: 0, qtys: [] };
  agg.unitsData['BALLS'].total += mathematicalValue;
  agg.unitsData['BALLS'].qtys.push(label);
  return; // Stop here so it doesn't run Step 5
}

    // 5. STANDARD CASE: Group by Unit within the Item
    if (!agg.unitsData[cleanUnit]) {
      agg.unitsData[cleanUnit] = { total: 0, qtys: [] };
    }

    agg.unitsData[cleanUnit].total += num;
    agg.unitsData[cleanUnit].qtys.push(num);
  };

  // --- START PROCESSING ORDERS ---
  filteredOrders.forEach(o => {
    // Handle Menu 1
    if (o.menu1Persons > 0) {
      menu1Items.forEach(item => {
        const portion = calculatePortion(o.menu1Persons, item);
        if (portion) {
          const { num, unit } = parseQty(portion);
          addToAggregate(item, num, unit);
        }
      });
    }

    // Handle Menu 2
    if (o.menu2Persons > 0) {
      menu2Items.forEach(item => {
        const portion = calculatePortion(o.menu2Persons, item);
        if (portion) {
          const { num, unit } = parseQty(portion);
          addToAggregate(item, num, unit);
        }
      });
    }

    // Handle A La Carte & Custom Owner Entries
    Object.entries(o.alaCarte || {}).forEach(([name, data]) => {
      let num = parseFloat(data.qty) || 0;
      let unit = data.chosenUnit || 'EA';
      addToAggregate(name, num, unit);
    });
  });

  // --- ORGANIZE BY CATEGORY ---
  const categories = {};
  itemAggregates.forEach(agg => {
    // Sort qtys for every unit inside this item
    Object.values(agg.unitsData).forEach(uData => {
      uData.qtys.sort((a, b) => {
        const valA = typeof a === 'number' ? a : parseFloat(a) || 0;
        const valB = typeof b === 'number' ? b : parseFloat(b) || 0;
        return valB - valA;
      });
    });

    if (!categories[agg.category]) categories[agg.category] = [];
    categories[agg.category].push(agg);
  });

  return categories;
};
//kitchen prep helper
const rawAggregates = computeKitchenAggregates();

// If alphabetical mode is ON, we pull all items out of their categories into one list
let displayData;
if (isAlphabetical) {
  const allItems = Object.values(rawAggregates).flat();
  displayData = {
    "All Items (A-Z)": allItems.sort((a, b) => a.itemName.localeCompare(b.itemName))
  };
} else {
  displayData = rawAggregates;
}
// Order totals calculation
const calculateOrderTotals = (order) => {
  let menuSubtotal = 0;
  if (order.menu1Persons > 0) menuSubtotal += 80 * order.menu1Persons;
  if (order.menu2Persons > 0) menuSubtotal += 90 * order.menu2Persons;

  let alaCarteSubtotal = 0;
  Object.entries(order.alaCarte || {}).forEach(([name, { qty }]) => {
    const normalizedName = name.trim().toUpperCase();
    const price = itemPrices[normalizedName]?.price || 0;
    alaCarteSubtotal += qty * price;
  });

  const deliveryFee = order.delivery ? (Number(order.deliveryFee) || 25) : 0;
  const subtotalBeforeTax = menuSubtotal + alaCarteSubtotal + deliveryFee;
  const taxableAmount = menuSubtotal + deliveryFee;
  const gst = taxableAmount * 0.05;
  const qst = taxableAmount * 0.09975;
  const totalTaxes = gst + qst;

  return {
    subtotal: subtotalBeforeTax,
    taxes: totalTaxes,
    total: subtotalBeforeTax + totalTaxes
  };
};

// ── CSV Export ───────────────────────────────────────────────────
const exportCurrentView = () => {
  const isAlpha = sortMode === 'alpha';
  const isDelivery = activeTab === 'delivery';

  if (activeTab === 'delivery') {
    const headers = ['Name', 'Phone', 'Address', 'Order #', 'Master ID'];
    const rows = displayList.map((o, idx) => {
      const address = [
        o.streetNumber || '',
        o.streetName || '',
        o.city || 'Montreal',
        o.postalCode || ''
      ].filter(Boolean).join(' ');
      return [
        `"${(o.name || 'UNKNOWN').replace(/"/g, '""')}"`,
        o.phone || '—',
        `"${address.replace(/"/g, '""')}"`,
        `#${o.orderNumber}`,
        isAlpha ? (idx + 1) : '—'
      ];
    });
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    // const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]); // UTF-8 BOM
const blob = new Blob([bom, csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'deliveries_list.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  const headers = (isAlpha && !isDelivery)
    ? ['Master #', 'Name', 'Phone', 'Order #', 'Type', 'Date', 'Time', 'Total Amount', 'Status']
    : ['Name', 'Phone', 'Order #', 'Type', 'Date', 'Time', 'Total Amount', 'Status'];

  const rows = displayList.map((o, idx) => {
  const base = [
    `"${(o.name || 'UNKNOWN').replace(/"/g, '""')}"`,
    o.phone || '—',
    `#${o.orderNumber}`,
    o.delivery ? 'Delivery' : 'Pickup',
    o.date || '—',
    o.time || '—',
    `$${Number(o.total || 0).toFixed(2)}`,
    o.printStatus || 'unprinted'
  ];
  // Only add Master # if alpha sort AND not deliveries
  return (isAlpha && !isDelivery) ? [idx + 1, ...base] : base;
});

  const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
  const bom = new Uint8Array([0xEF, 0xBB, 0xBF]); // UTF-8 BOM
const blob = new Blob([bom, csv], { type: 'text/csv;charset=utf-8;' });
//   const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${activeTab}_list.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
const exportTotalsCSV = () => {
  const summaryOrders = getFilteredSummaryOrders();
  const aggregates = computeKitchenAggregates(summaryOrders);

  // Headers
  const headers = ['Category', 'Product', 'Total Quantity', 'Unit'];

  // Rows
  const rows = [];
  Object.entries(aggregates).forEach(([category, items]) => {
    items.forEach(item => {
      rows.push([
        category,
        item.itemName,
        item.total,
        item.unit
      ]);
    });
  });

  // CSV string
  const csv = [headers, ...rows].map(row => row.join(',')).join('\n');

  // Download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'kitchen_totals_summary.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
// ── Admin Auth & Data Fetch ──────────────────────────────────────
const adminLogin = async () => {
  try {
    const res = await fetch(`${baseURL}/${adminPath}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: adminPass }),
    });
    if (!res.ok) throw new Error();
    const data = await res.json();
    setAdminToken(data.token);
    console.log('Logged in with token:', data.token);
    setIsAdmin(true);
  } catch {
    Swal.fire('Error', 'Invalid Password', 'error');
  }
};

useEffect(() => {
  if (isAdmin && adminToken) {
    console.log('Fetching orders with token:', adminToken.substring(0, 10) + '...');
    fetchData();
  } else {
    setOrders([]);
  }
}, [isAdmin, adminToken, filterDate, filterType, activeTab]);

const fetchData = async () => {
  if (!adminToken) return;
  const query = `?date=${filterDate}${filterType !== 'All' ? `&type=${filterType.toLowerCase()}` : ''}`;
  try {
    const res = await fetch(`${baseURL}/${adminPath}/orders${query}`, {
      headers: { Authorization: `Bearer ${adminToken}` }
    });
    if (!res.ok) {
      if (res.status === 401) {
        Swal.fire('Session Expired', 'Please log in again.', 'warning');
        setIsAdmin(false);
        setAdminToken('');
        return;
      }
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    setOrders(data);
  } catch (err) {
    console.error('Fetch orders error:', err);
    Swal.fire('Error', 'Failed to load orders. Check server.', 'error');
  }
};

// ── Cancel / Archive ─────────────────────────────────────────────
// --- 2. ACTIONS / FUNCTIONS (Put it here!) ---
  const handleCancel = async (id) => {
  const result = await Swal.fire({
    title: 'Archive this order?',
    text: "This will move it to Cancelled Orders. Kitchen prep list will be updated.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, archive it!',
    cancelButtonText: 'No, keep it active'
  });

  if (!result.isConfirmed) return;

  try {
    const res = await fetch(`${baseURL}/${adminPath}/orders/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminToken}`
      },
      body: JSON.stringify({ status: 'cancelled' })
    });

    if (res.ok) {
      // Refresh data so the order moves tabs
      await fetchData();

      // Get the order details from the fresh list to show accurate info in toast
      const cancelledOrder = orders.find(o => o._id === id);

      Swal.fire({
        title: 'Order Archived!',
        html: `Order <strong>#${cancelledOrder?.orderNumber || id}</strong> for <strong>${cancelledOrder?.name || 'the customer'}</strong> has been cancelled.<br><br>You can now find it in the <strong>Cancelled Orders</strong> tab.`,
        icon: 'success',
        timer: 4000,                // Auto-close after 4 seconds
        showConfirmButton: false,   // No OK button needed
        toast: true,                // Makes it a nice toast in top-right
        position: 'top-end',        // Top-right corner
        timerProgressBar: true
      });
    } else {
      const errorText = await res.text();
      console.error('Soft cancel failed:', res.status, errorText);
      Swal.fire('Error', `Failed to archive: ${res.status} - ${errorText}`, 'error');
    }
  } catch (err) {
    console.error('Cancel network error:', err);
    Swal.fire('Error', 'Network issue – check console or server', 'error');
  }
};
const handlePermanentDelete = async (id) => {
  const result = await Swal.fire({
    title: 'Permanent Delete?',
    html: 'This will <strong>permanently erase</strong> the order from the database.<br><br>No recovery possible.',
    icon: 'error',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete forever!',
    cancelButtonText: 'No, keep it'
  });

  if (!result.isConfirmed) return;

  try {
    console.log(`[PERM DELETE] Deleting order ID: ${id}`);

    const res = await fetch(`${baseURL}/${adminPath}/orders/${id}/permanent`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${adminToken}`,
        'Content-Type': 'application/json'
      }
    });

    console.log(`[PERM DELETE] Response status: ${res.status}`);

    if (res.ok) {
      await fetchData(); // Refresh list

      // Force re-render so Cancelled count updates immediately
      setRefreshKey(prev => prev + 1);

      Swal.fire({
        title: 'Order Permanently Deleted',
        html: `Order has been completely removed from the system.`,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        timerProgressBar: true
      });
    } else {
      const errorText = await res.text();
      console.error('[PERM DELETE] Failed:', res.status, errorText);
      Swal.fire('Error', `Delete failed: ${res.status} - ${errorText || 'Unknown'}`, 'error');
    }
  } catch (err) {
    console.error('[PERM DELETE] Network error:', err);
    Swal.fire('Error', 'Network issue – check console/server', 'error');
  }
};
// const optimizeDeliveryRoute = async () => {
//   const deliveries = getFilteredSummaryOrders().filter(o => o.delivery);
  
//   // Transform orders into VROOM-friendly 'jobs'
//   const jobs = deliveries.map((order, index) => ({
//     id: index,
//     description: order.orderNumber,
//     location: [order.lng, order.lat], // Coordinates must be [longitude, latitude]
//     service: 300, // 5 mins per drop-off
//     delivery: [1] // Represents 1 'unit' of food (optional)
//   }));

//   const payload = {
//     jobs: jobs,
//     vehicles: [
//       {
//         id: 1,
//         profile: 'car',
//         start: [-73.5673, 45.5017], // Paradise Kosher HQ
//         end: [-73.5673, 45.5017],
//         capacity: [50] // Max 50 orders per trip
//       }
//     ]
//   };

//   try {
//     const response = await fetch('https://your-vroom-service.onrender.com', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify(payload),
//     });

//     const data = await response.json();

//     if (data.code === 0) {
//       // Map the returned 'steps' back to your original delivery objects
//       const optimized = data.routes[0].steps
//         .filter(step => step.type === 'job')
//         .map(step => deliveries[step.id]);

//       setOptimizedDeliveries(optimized);
      
//       const totalMinutes = Math.round(data.summary.duration / 60);
//       Swal.fire('Route Ready', `Estimated travel time: ${totalMinutes} mins.`, 'success');
//     }
//   } catch (error) {
//     Swal.fire('Routing Failed', 'Check if the Render server is awake.', 'error');
//   }
// };
// const optimizeDeliveryRoute = async () => {
//   const deliveries = getFilteredSummaryOrders().filter(o => o.delivery);

//   if (deliveries.length === 0) {
//     Swal.fire('No Deliveries', 'No delivery orders for this date/filter.', 'info');
//     return;
//   }

//   Swal.fire({
//     title: 'Geocoding...',
//     text: 'Locating Montreal addresses...',
//     allowOutsideClick: false,
//     didOpen: () => { Swal.showLoading(); }
//   });

//   const jobs = [];
  
//   try {
//     // 1. Convert addresses to coordinates
//     for (let i = 0; i < deliveries.length; i++) {
//       const order = deliveries[i];
//       const coords = await getCoords(order.address || order.streetName); 
      
//       if (coords) {
//         jobs.push({
//           id: i,
//           location: [coords.lng, coords.lat],
//           service: 300 
//         });
//       }
//       await new Promise(resolve => setTimeout(resolve, 1000));
//     }

//     if (jobs.length === 0) {
//       Swal.fire('Error', 'Could not locate any addresses.', 'error');
//       return;
//     }

//     // 2. Call VROOM Docker Engine
//     const vroomPayload = {
//   jobs: jobs,
//   vehicles: [{
//     id: 1,
//     profile: 'car',
//     // 11600 Salaberry Blvd, DDO
//     start: [-73.8183, 45.4924], 
//     end: [-73.8183, 45.4924]
//   }]
// };

//     const response = await fetch('http://localhost:3000', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify(vroomPayload),
//     });

//     const result = await response.json();

//     if (result.code === 0) {
//       // 3. Sort the actual order objects based on VROOM's results
//       const optimizedOrderList = result.routes[0].steps
//         .filter(step => step.type === 'job')
//         .map(step => deliveries[step.id]);

//       setOptimizedDeliveries(optimizedOrderList);
// //Add this line to scroll the user down to the results automatically
// setTimeout(() => {
//   const element = document.getElementById("delivery-manifest-section");
//   if (element) element.scrollIntoView({ behavior: 'smooth' });
// }, 500);
//       // 4. Calculate distance safely to avoid NaN
//       const distanceRaw = result.summary?.distance || 0;
//       const distanceKm = (distanceRaw / 1000).toFixed(1);

//       if (distanceRaw === 0) {
//          Swal.fire('Success!', 'Route sorted! (Distance info unavailable)', 'success');
//       } else {
//          Swal.fire('Success!', `Route optimized: ${distanceKm}km.`, 'success');
//       }
//     } else {
//       Swal.fire('VROOM Error', `Code: ${result.code}. Check Docker logs.`, 'error');
//     }

//   } catch (error) {
//     console.error("Route Error:", error);
//     Swal.fire('Error', 'Optimization failed. Is Docker on Port 3000?', 'error');
//   }
// };
// const optimizeDeliveryRoute = async () => {
//   // 1. Get only active delivery orders (exclude cancelled)
//   const rawDeliveries = getFilteredSummaryOrders().filter(o => 
//     Boolean(o.delivery) && o.status !== 'cancelled'
//   );

//   if (rawDeliveries.length === 0) {
//     Swal.fire('No Deliveries', 'No delivery orders found to optimize.', 'info');
//     return;
//   }

//   Swal.fire({
//     title: 'Optimizing Route',
//     text: `Geocoding ${rawDeliveries.length} addresses and calculating best path...`,
//     allowOutsideClick: false,
//     didOpen: () => Swal.showLoading()
//   });

//   try {
//     const jobs = [];
//     const validOrders = [];

//     // 2. Geocode loop - Converting addresses to coordinates
//     for (const order of rawDeliveries) {
//       // Use normalizer to get GPS-friendly string (No Apt numbers for the search)
//       const normalized = normalizeAddress(order);
//       const searchAddress = typeof normalized === 'object' ? normalized.forGPS : normalized;
//       const displayAddress = typeof normalized === 'object' ? normalized.full : normalized;

//       // Get Coordinates from your Geocoder (Mapbox/Google)
//       const geoResult = await getCoords(searchAddress);

//       // SAFETY: Skip if address is invalid or missing
//       if (!geoResult || !geoResult.lng || !geoResult.lat) {
//         console.warn(`Skipping Order #${order.orderNumber} - Invalid Address: ${searchAddress}`);
//         continue; 
//       }

//       // Create the coordinate array [lng, lat]
//       // renamed to 'coordsArray' to avoid 'window.location' naming conflict
//       const coordsArray = [parseFloat(geoResult.lng), parseFloat(geoResult.lat)];

//       // 3. Add to VROOM Jobs list with PRIORITY
//       // We give DDO/Pierrefonds priority 100 so they happen FIRST
//       const isLocal = /ddo|pierrefonds|dollard/i.test(order.city || '');
      
//       jobs.push({
//         id: jobs.length, 
//         description: `${order.name} (#${order.orderNumber})`,
//         location: coordsArray,
//         priority: isLocal ? 100 : 0 
//       });

//       // Keep track of the order data to map it back later
//       validOrders.push({
//         ...order,
//         cleanAddress: displayAddress,
//         coords: coordsArray
//       });
//     }

//     if (jobs.length === 0) {
//       throw new Error("None of the addresses could be located. Please check the address formats.");
//     }

//     // 4. Call your VROOM Server
//     const response = await fetch('https://vroom-passover.onrender.com', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({
//         jobs: jobs,
//         vehicles: [{
//           id: 1,
//           profile: 'auto',
//           start: [-73.82056, 45.48956], // Your store (Paradise Kosher)
//           // Removing 'end' forces VROOM to finish at the last house 
//           // instead of looping back to the store at the end of the day.
//           capacity: [100]
//         }]
//       })
//     });

//     if (!response.ok) {
//       const errorText = await response.text();
//       throw new Error(`VROOM Server Error: ${errorText}`);
//     }

//     const data = await response.json();
//     const route = data.routes?.[0];

//     if (!route || !route.steps) {
//       throw new Error("VROOM could not calculate a valid route.");
//     }

//     // 5. Reorder the deliveries based on VROOM's optimized sequence
//     const orderedDeliveries = route.steps
//       .filter(step => step.type === 'job')
//       .map(step => validOrders[step.id]);

//     // Update your React state with the new sorted list
//     setOptimizedDeliveries(orderedDeliveries);

//     Swal.fire({
//       icon: 'success',
//       title: 'Route Optimized!',
//       text: `${orderedDeliveries.length} stops organized. DDO/Pierrefonds prioritized first.`,
//       timer: 2500
//     });

//   } catch (err) {
//     console.error("Optimization failed:", err);
//     Swal.fire({
//       icon: 'error',
//       title: 'Routing Failed',
//       text: err.message || 'Check your local VROOM server connection.'
//     });
//   }
// };
const optimizeDeliveryRoute = async () => {
  const rawDeliveries = getFilteredSummaryOrders().filter(
    (o) => Boolean(o.delivery) && o.status !== "cancelled"
  );

  if (rawDeliveries.length === 0) {
    Swal.fire("No Deliveries", "No delivery orders found to optimize.", "info");
    return;
  }

  Swal.fire({
    title: "Optimizing Route",
    text: `Geocoding ${rawDeliveries.length} addresses and calculating best path...`,
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

 try {
  const jobs = [];
  const validOrders = [];

  // 1. Geocoding Loop
  for (let i = 0; i < rawDeliveries.length; i++) {
    const order = rawDeliveries[i];
    const normalized = normalizeAddress(order);
    const searchAddress = typeof normalized === "object" ? normalized.forGPS : normalized;
    const displayAddress = typeof normalized === "object" ? normalized.full : normalized;

    const geoResult = await getCoords(searchAddress);

    if (!geoResult || geoResult.lng === undefined) continue;

    // IMPORTANT: Geoapify wants [lng, lat] for the API call
    const lngLat = [parseFloat(geoResult.lng), parseFloat(geoResult.lat)];
    
    // Create a consistent ID for matching
    const internalId = order._id || `job_${i}`;

    jobs.push({
      id: internalId,
      location: lngLat,
    });

    validOrders.push({
      ...order,
      cleanAddress: displayAddress,
      lng: geoResult.lng, // Store separately to avoid confusion
      lat: geoResult.lat,
      routeId: internalId // Use this for the match
    });
  }

  // 2. Call Geoapify Bridge
  const response = await fetch("https://geoapify-paradise.onrender.com/optimize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      drivers: [{ id: "driver_1", start_location: [-73.8197, 45.4913] }],
      jobs,
    }),
  });

  const data = await response.json();

  // 3. Robust Response Parsing 
  // Geoapify/VROOM usually returns steps/actions in trip.properties
  const routeSteps = data.features?.[0]?.properties?.actions || 
                     data.routes?.[0]?.steps || 
                     [];

  let orderedDeliveries = [];

  if (routeSteps.length > 0) {
    orderedDeliveries = routeSteps
      .filter(step => step.type === "delivery" || step.job_id) // Ignore "start" and "end" steps
      .map((step) => {
        const targetId = step.job_id || step.id;
        return validOrders.find(order => order.routeId === targetId);
      })
      .filter(Boolean);
  }

  // Final Safety Fallback
  if (orderedDeliveries.length === 0) {
    console.warn("API returned no valid steps, falling back to original order");
    orderedDeliveries = validOrders;
  }

  setOptimizedDeliveries(orderedDeliveries);

    Swal.fire({
      icon: "success",
      title: "Route Optimized!",
      text: `${orderedDeliveries.length} stops organized successfully.`,
      timer: 2500,
    });
  } catch (err) {
    console.error("Optimization failed:", err);
    Swal.fire({
      icon: "error",
      title: "Routing Failed",
      text: err.message || "Check your route API connection.",
    });
  }
};

// End of Part 2/3 ───────────────────────────────────────────────
// const publishRoute = async (driverNumber) => {
//   const sourceData = (optimizedStops && optimizedStops.length > 0) ? optimizedStops : optimizedDeliveries;
//   const driverStops = sourceData.filter(d => String(d.assignedDriver) === String(driverNumber));

//   if (driverStops.length === 0) {
//     return Swal.fire('Wait!', `Assign addresses to Driver ${driverNumber} first.`, 'warning');
//   }

//   // 1. GET THE REAL DATE: Grab 'date' from the first delivery (e.g., "2026-03-30")
//   // We format it to your preferred "DD-MM-YYYY" or keep "YYYY-MM-DD"
//   const rawDate = sourceData[0]?.date || new Date().toISOString().split('T')[0];
//   const cleanId = `${rawDate}-driver-${driverNumber}`; 

//   try {
//     // 2. FETCH: Ensure the URL matches your backend route exactly
//     const response = await fetch('/api/public-routes', { 
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ 
//         routeId: cleanId, 
//         driverNumber, 
//         stops: driverStops,
//         date: rawDate
//       }),
//     });

//     if (response.ok) {
//       Swal.fire({ icon: 'success', title: 'Published!', text: `Route: ${cleanId}`, timer: 2000, showConfirmButton: false });
//       window.open(`/manifest/${cleanId}`, '_blank');
//     } else {
//       // If this triggers, your backend isn't sending JSON back
//       const text = await response.text(); 
//       console.error("Server returned HTML instead of JSON:", text);
//     }
//   } catch (error) {
//     Swal.fire('Error', 'Server connection failed', 'error');
//   }
// };
const publishRoute = async (driverNumber) => {
  // 1. Get the raw data first
  const sourceData = (optimizedStops && optimizedStops.length > 0) ? optimizedStops : optimizedDeliveries;
  const driverStops = sourceData.filter(d => String(d.assignedDriver) === String(driverNumber));

  if (driverStops.length === 0) return Swal.fire('Error', 'No stops for this driver', 'error');

  // --- 🚛 INSERT THE MAPPING HERE ---
  const stopsToSave = driverStops.map(order => {
    // This calls the 'flawless' logic you exported from DeliveryManifest
    const { fullAddress } = getOrderAddresses(order); 
    
    return {
      ...order,
      fullAddress: fullAddress, // This is the 'Magic String' the driver needs
      lat: order.lat,
      lng: order.lng,
      customer: order.customer || order.name
    };
  });
  // ----------------------------------

  // 2. Now send 'stopsToSave' instead of 'driverStops'
  try {
    const rawDate = sourceData[0]?.date || "2026-03-30";
    const cleanId = `${rawDate.split('-').reverse().join('-')}-driver-${driverNumber}`;

    const response = await fetch('/api/public-routes', { 
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        routeId: cleanId, 
        stops: stopsToSave, // <--- USE THE MAPPED DATA HERE
        polyline: optimizedRoute?.polyline || [] 
      }),
    });

    if (response.ok) {
       window.open(`/manifest/${cleanId}`, '_blank');
       Swal.fire('Success', 'Route Published!', 'success');
    }
  } catch (error) {
    console.error("Publish Error:", error);
  }
};
const copyDriverLink = (driverNumber) => {
  const sourceData = (optimizedStops && optimizedStops.length > 0) ? optimizedStops : optimizedDeliveries;
  const rawDate = sourceData[0]?.date || new Date().toISOString().split('T')[0];
  const cleanId = `${rawDate}-driver-${driverNumber}`;
  
  const url = `${window.location.origin}/manifest/${cleanId}`;
  navigator.clipboard.writeText(url);
  
  Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Link Copied!', timer: 2000, showConfirmButton: false });
};

// ──────────────────────────────────────────────────────────────
// PART 3/3 – JSX Return, Tabs, Views, Kitchen & Archive Rendering
// ──────────────────────────────────────────────────────────────

  // Login screen
  if (!isAdmin) {
    return (
      <div className="container mt-5">
        <div className="card shadow p-5 w-50 mx-auto text-center">
          <img
            src={process.env.PUBLIC_URL + '/images/logo-removebg-preview.png'}
            alt="Paradise Kosher Logo"
            style={{
              maxWidth: '180px',
              height: 'auto',
              margin: '0 auto 20px auto',
              display: 'block'
            }}
          />
          <h3>Admin Dashboard</h3>
          <input
            type="password"
            placeholder="Password"
            onChange={(e) => setAdminPass(e.target.value)}
            className="form-control my-3"
          />
          <button className="btn btn-primary w-100" onClick={adminLogin}>
            Login
          </button>
        </div>

        <div style={{ marginTop: '20px', fontSize: '14px', color: '#333', textAlign: 'center' }}>
          Webmaster Design & Development | Copyright 2026 | by
          <br />
          <a
            href="https://devlogic.ca"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#0d6efd',
              textDecoration: 'none',
              fontWeight: '700',
              borderBottom: '2px solid #0d6efd'
            }}
          >
            DEVLOGIC
          </a>
        </div>
      </div>
    );
  }

// Enhanced handleEdit with editable invoice modal + corrected deposit display
const handleEdit = async (order) => {
  let currentOrder = { ...order };

  const { value: result } = await Swal.fire({
    title: 'Edit Invoice',
    html: `
      <div style="max-height: 60vh; overflow-y: auto; font-size: 16px;">
        <table class="table table-bordered" id="edit-invoice-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Unit Price</th>
              <th>Qty</th>
              <th>Unit</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody id="invoice-body">
            ${currentOrder.menu1Persons > 0 ? `
              <tr data-type="menu" data-menu="1">
                <td>Menu #1</td>
                <td>$80</td>
                <td><input type="number" class="form-control qty" value="${currentOrder.menu1Persons}" min="0"></td>
                <td>people</td>
                <td>$${ (currentOrder.menu1Persons * 80).toFixed(2) }</td>
                <td></td>
              </tr>` : ''}
            ${currentOrder.menu2Persons > 0 ? `
              <tr data-type="menu" data-menu="2">
                <td>Menu #2</td>
                <td>$90</td>
                <td><input type="number" class="form-control qty" value="${currentOrder.menu2Persons}" min="0"></td>
                <td>people</td>
                <td>$${ (currentOrder.menu2Persons * 90).toFixed(2) }</td>
                <td></td>
              </tr>` : ''}
            ${Object.entries(currentOrder.alaCarte || {}).map(([name, item], i) => {
              const savedPrice = item.price || itemPrices[name.toUpperCase()]?.price || 0;
              const qty = item.qty || 0;
              return `
                <tr data-type="alacarte" data-idx="${i}">
                  <td><input type="text" class="form-control name" value="${name}"></td>
                  <td><input type="number" class="form-control price" value="${savedPrice}" step="0.01"></td>
                  <td><input type="number" class="form-control qty" value="${qty}" min="0"></td>
                  <td><input type="text" class="form-control unit" value="${item.chosenUnit || 'lb'}"></td>
                  <td class="line-total">$${(qty * savedPrice).toFixed(2)}</td>
                  <td><button class="btn btn-sm btn-danger remove-row">×</button></td>
                </tr>
              `;
            }).join('')}
            ${currentOrder.delivery ? `
              <tr data-type="delivery">
                <td>Delivery Fee</td>
                <td><input type="number" class="form-control price" value="25"></td>
                <td>1</td>
                <td></td>
                <td class="line-total">$25.00</td>
                <td><button class="btn btn-sm btn-danger remove-row">×</button></td>
              </tr>` : ''}
          </tbody>
        <tfoot>
  <tr>
    <td colspan="4"><strong>Subtotal</strong></td>
    <td id="subtotal">$0.00</td>
    <td></td>
  </tr>
  <tr>
    <td colspan="4">GST (5%)</td>
    <td id="gst">$0.00</td>
    <td></td>
  </tr>
  <tr>
    <td colspan="4">QST (9.975%)</td>
    <td id="qst">$0.00</td>
    <td></td>
  </tr>
  <tr>
    <td colspan="4"><strong>Total</strong></td>
    <td id="total-before-deposit">$0.00</td>
    <td></td>
  </tr>
  <!-- Deposit row - controlled by JS -->
  <tr id="deposit-row" style="display: none;">
    <td colspan="4"><strong>Deposit Paid</strong></td>
    <td id="deposit-total" class="text-danger">-$0.00</td>
    <td></td>
  </tr>
  <tr>
    <td colspan="4">
      <strong id="balance-label">Balance Due</strong>
      <span id="paid-in-full-badge" class="badge bg-success ms-2" style="display:none;">Paid in Full</span>
    </td>
    <td id="total">$0.00</td>
    <td></td>
  </tr>
</tfoot>
        </table>

        <!-- Existing Add Item Section -->
        <div class="mt-3">
          <select id="add-item-select" class="form-select d-inline-block w-50">
            <option value="">+ Add Item...</option>
            ${Object.keys(itemPrices).map(k => `<option value="${k}">${k}</option>`).join('')}
            <option value="custom">Custom Item</option>
            <option value="MENU #1">Menu #1 Selection</option>
            <option value="MENU #2">Menu #2 Selection</option>
            <option value="DELIVERY">Delivery Fee</option>
          </select>
          <button id="add-item-btn" class="btn btn-success ms-2">Add</button>
        </div>

        <!-- Date & Time -->
        <div class="mt-3">
          <label>Date:</label>
          <input type="date" id="edit-date" value="${currentOrder.date || ''}" class="form-control d-inline-block w-auto ms-2">
          <label class="ms-3">Time (pickup):</label>
          <input type="time" id="edit-time" value="${currentOrder.time || ''}" class="form-control d-inline-block w-auto ms-2">
        </div>
<div class="mt-4 p-3" style="border: 2px solid #333; background: #fff; border-radius: 8px;">
  <h6 style="font-weight: bold; border-bottom: 1px solid #ccc; padding-bottom: 5px;">Client Information & Delivery Address</h6>
  <div class="row g-2">
    <div class="col-md-6">
      <label class="form-label small mb-0">Full Name</label>
      <input type="text" id="edit-name" value="${currentOrder.name || ''}" class="form-control" placeholder="Full Name">
    </div>
    <div class="col-md-6">
      <label class="form-label small mb-0">Phone Number</label>
      <input type="tel" id="edit-phone" value="${currentOrder.phone || ''}" class="form-control" placeholder="Phone">
    </div>

    <div class="col-md-3">
      <label class="form-label small mb-0">Street #</label>
      <input type="text" id="edit-streetNumber" value="${currentOrder.streetNumber || ''}" class="form-control" placeholder="123">
    </div>
    <div class="col-md-9">
      <label class="form-label small mb-0">Street Name</label>
      <input type="text" id="edit-streetName" value="${currentOrder.streetName || ''}" class="form-control" placeholder="Main St">
    </div>

    <div class="col-md-6">
      <label class="form-label small mb-0">City</label>
      <input type="text" id="edit-city" value="${currentOrder.city || ''}" class="form-control" placeholder="Montreal">
    </div>
    <div class="col-md-6">
      <label class="form-label small mb-0">Postal Code</label>
      <input type="text" id="edit-postalCode" value="${currentOrder.postalCode || ''}" class="form-control" placeholder="H1A 1A1">
    </div>

    <div class="col-md-6">
      <label class="form-label small mb-0">Email</label>
      <input type="email" id="edit-email" value="${currentOrder.email || ''}" class="form-control" placeholder="Email">
    </div>
    <div class="col-md-6">
      <label class="form-label small mb-0">Company (optional)</label>
      <input type="text" id="edit-company" value="${currentOrder.company || ''}" class="form-control" placeholder="Company Name">
    </div>
  </div>
</div>

        <!-- Deposit Field -->
        <div class="mt-3">
          <label class="form-label small mb-0">Deposit Paid ($)</label>
          <input type="number" id="edit-deposit" value="${currentOrder.deposit || 0}" step="0.01" min="0" class="form-control w-50 d-inline-block">
        </div>

        <!-- Resend Invoice Button -->
        <div class="mt-3">
          <button id="resend-invoice-btn" class="btn btn-outline-primary">
            Resend Updated Invoice
          </button>
        </div>

        <!-- Payment Details -->
        <div class="mt-4 p-3" style="border: 2px solid #333; background: #fff; border-radius: 8px;">
          <h6 style="font-weight: bold; border-bottom: 1px solid #ccc; padding-bottom: 5px;">
            Payment Details (For Delivery/Manual Processing)
          </h6>
          <div class="row g-2">
            <div class="col-md-5">
              <label class="form-label small mb-0">Name on Card</label>
              <input type="text" id="edit-cardName" value="${currentOrder.cardName || ''}" class="form-control" placeholder="Full Name">
            </div>
            <div class="col-md-5">
              <label class="form-label small mb-0">Credit Card Number</label>
              <input type="text" id="edit-ccNumber" value="${currentOrder.ccNumber || ''}" class="form-control" inputmode="numeric" placeholder="16 Digits Only">
            </div>
            <div class="col-md-2">
              <label class="form-label small mb-0">Expiry</label>
              <input type="text" id="edit-expDate" value="${currentOrder.expDate || ''}" class="form-control" inputmode="numeric" placeholder="MM/YY" maxlength="5">
            </div>
          </div>
        </div>
      </div>
    `,
    width: '900px',
    showCancelButton: true,
    confirmButtonText: 'Save Changes',
    didOpen: () => {
      const body = document.getElementById('invoice-body');
      const addBtn = document.getElementById('add-item-btn');
      const select = document.getElementById('add-item-select');
      const depositInput = document.getElementById('edit-deposit');
      const depositRow = document.getElementById('deposit-row');
      const depositTotal = document.getElementById('deposit-total');

      const recalc = () => {
        if (!body) return;

        let menuSubtotal = 0;
        let alaCarteSubtotal = 0;
        let taxableAlaCarte = 0;
        let deliveryFee = 0;
        let deposit = Number(depositInput?.value) || 0;

        // Menus
        body.querySelectorAll('tr[data-type="menu"]').forEach(row => {
          const qty = Number(row.querySelector('.qty')?.value) || 0;
          const price = row.dataset.menu === "1" ? 80 : 90;
          const line = qty * price;
          row.cells[4].textContent = `$${line.toFixed(2)}`;
          menuSubtotal += line;
        });

        // À la carte
        body.querySelectorAll('tr[data-type="alacarte"]').forEach(row => {
          const name = (row.querySelector('.name')?.value || "").trim().toUpperCase();
          const qty = Number(row.querySelector('.qty')?.value) || 0;
          const price = Number(row.querySelector('.price')?.value) || 0;
          const line = qty * price;
          row.cells[4].textContent = `$${line.toFixed(2)}`;
          alaCarteSubtotal += line;
          if (name === 'FANCY SEDER PLATES' || itemPrices[name]?.taxable === true) {
            taxableAlaCarte += line;
          }
        });

        // Delivery
        const dRow = body.querySelector('tr[data-type="delivery"]');
        if (dRow) {
          deliveryFee = Number(dRow.querySelector('.price')?.value) || 0;
          dRow.querySelector('.line-total').textContent = `$${deliveryFee.toFixed(2)}`;
        }

        const subtotal = menuSubtotal + alaCarteSubtotal + deliveryFee;
        const taxableAmount = menuSubtotal + deliveryFee + taxableAlaCarte;
        const gst = taxableAmount * 0.05;
        const qst = taxableAmount * 0.09975;
        const totalBeforeDeposit = subtotal + gst + qst;
        const totalAfterDeposit = totalBeforeDeposit - deposit;

        document.getElementById('subtotal').textContent = `$${subtotal.toFixed(2)}`;
        document.getElementById('gst').textContent = `$${gst.toFixed(2)}`;
        document.getElementById('qst').textContent = `$${qst.toFixed(2)}`;
        document.getElementById('total-before-deposit').textContent = `$${totalBeforeDeposit.toFixed(2)}`;
        document.getElementById('total').textContent = `$${totalAfterDeposit.toFixed(2)}`;
// Show/hide deposit row
  const depositRow = document.getElementById('deposit-row');
  const depositTotal = document.getElementById('deposit-total');
  if (depositRow) {
    if (deposit > 0) {
      depositRow.style.display = '';
      if (depositTotal) {
        depositTotal.textContent = `-$${deposit.toFixed(2)}`;
      }
    } else {
      depositRow.style.display = 'none';
    }
  }

  // Paid in Full / Overpaid badge
  const paidInFullBadge = document.getElementById('paid-in-full-badge');
  const balanceLabel = document.getElementById('balance-label');
  if (totalAfterDeposit <= 0.01) {  // ← tolerance of 1 cent (or 0.01)
  paidInFullBadge.style.display = 'inline-block';
  paidInFullBadge.textContent = totalAfterDeposit < -0.01 ? 'Overpaid' : 'Paid in Full';
  paidInFullBadge.className = totalAfterDeposit < -0.01 ? 'badge bg-warning ms-2' : 'badge bg-success ms-2';
  balanceLabel.textContent = 'Balance Due';
} else {
  paidInFullBadge.style.display = 'none';
  balanceLabel.textContent = 'Total Due';
}
  
};
      // Add Item logic (unchanged)
      addBtn.addEventListener('click', () => {
        // ... your existing add item code ...
        recalc();
        select.value = '';
      });

      body.addEventListener('click', e => {
        if (e.target.classList.contains('remove-row')) {
          e.target.closest('tr').remove();
          recalc();
        }
      });

      // Real-time recalc on any input, including deposit
      body.addEventListener('input', recalc);
      if (depositInput) depositInput.addEventListener('input', recalc);
      recalc();

      // Resend Invoice Button
      document.getElementById('resend-invoice-btn').addEventListener('click', () => {
        const updated = { ...currentOrder };
        // Re-grab current values
        updated.menu1Persons = Number(body.querySelector('tr[data-menu="1"] .qty')?.value) || 0;
        updated.menu2Persons = Number(body.querySelector('tr[data-menu="2"] .qty')?.value) || 0;
        updated.deposit = Number(depositInput.value) || 0;
        updated.name = document.getElementById('edit-name').value.trim();
        updated.phone = document.getElementById('edit-phone').value.trim();
        updated.email = document.getElementById('edit-email').value.trim();
        updated.company = document.getElementById('edit-company').value.trim();
        // ... add other fields if needed ...
        handlePrint(updated);
        Swal.fire({
          title: 'Invoice Resent',
          text: 'Updated receipt has been printed with latest changes (including deposit).',
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      });

      // ... existing CC/expiry masking code ...
    },
    preConfirm: () => {
      const body = document.getElementById('invoice-body');
      const updated = { ...currentOrder };

      // Menus
      const m1 = body.querySelector('tr[data-menu="1"] .qty');
      updated.menu1Persons = m1 ? Number(m1.value) : 0;
      const m2 = body.querySelector('tr[data-menu="2"] .qty');
      updated.menu2Persons = m2 ? Number(m2.value) : 0;

      // Ala Carte
      updated.alaCarte = {};
      body.querySelectorAll('tr[data-type="alacarte"]').forEach(row => {
        const name = row.querySelector('.name').value.trim();
        const qty = Number(row.querySelector('.qty').value);
        const unit = row.querySelector('.unit').value.trim();
        const price = Number(row.querySelector('.price').value);
        if (name && qty > 0) {
          updated.alaCarte[name] = { qty, chosenUnit: unit, price };
        }
      });

      // Delivery
      const dRow = body.querySelector('tr[data-type="delivery"]');
      updated.delivery = !!dRow;
      updated.deliveryFee = dRow ? Number(dRow.querySelector('.price').value) : 0;

      // Deposit
      updated.deposit = Number(document.getElementById('edit-deposit').value) || 0;

      // Client Info
      updated.name = document.getElementById('edit-name').value.trim();
      updated.phone = document.getElementById('edit-phone').value.trim();
      updated.email = document.getElementById('edit-email').value.trim();
      updated.company = document.getElementById('edit-company').value.trim();
      // Add these specific lines to capture the new inputs
  updated.streetNumber = document.getElementById('edit-streetNumber').value.trim();
  updated.streetName = document.getElementById('edit-streetName').value.trim();
  updated.city = document.getElementById('edit-city').value.trim();
  updated.postalCode = document.getElementById('edit-postalCode').value.trim();

      // Final Totals
      updated.subtotal = parseFloat(document.getElementById('subtotal').textContent.replace('$', ''));
      updated.taxes = parseFloat(document.getElementById('gst').textContent.replace('$', '')) +
                      parseFloat(document.getElementById('qst').textContent.replace('$', ''));
      updated.total = parseFloat(document.getElementById('total').textContent.replace('$', ''));

      updated.date = document.getElementById('edit-date').value;
      updated.time = document.getElementById('edit-time').value;

      // Payment Details
      updated.cardName = document.getElementById('edit-cardName').value.trim();
      updated.ccNumber = document.getElementById('edit-ccNumber').value.trim();
      updated.expDate = document.getElementById('edit-expDate').value.trim();

      return updated;
    }
  });

  if (result) {
    console.log(">>> DATA BEING SENT TO SERVER:", result);
    console.log(">>> TARGET URL:", `${baseURL}/${adminPath}/orders/${order._id}`);

    try {
      const response = await fetch(`${baseURL}/${adminPath}/orders/${order._id}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${adminToken}` 
        },
        body: JSON.stringify(result)
      });

      if (response.status === 404) {
        console.error("ERROR: The server says this Order ID does not exist.");
        return Swal.fire('Error', 'Order not found on server (404)', 'error');
      }

      if (response.ok) {
        console.log(">>> SAVE SUCCESSFUL");
        await fetchData(); 
        Swal.fire('Saved', 'Order updated successfully', 'success');
      }
    } catch (err) {
      console.error(">>> NETWORK ERROR:", err);
      Swal.fire('Error', 'Connection failed', 'error');
    }
  }
}; // This closes the handleEdit function

  // handlePrint (unchanged)
 const handlePrint = async (order) => {
  // --- TROUBLESHOOTING LOGS ---
  // Press F12 in your browser and click "Console" to see these!
  console.log("--- PRINT DEBUG START ---");
  console.log("Full Order Object from DB:", order);
  console.log("Subtotal value:", order.subtotal);
  console.log("Taxes value:", order.taxes);

  const win = window.open('', '', 'height=1100,width=900');
  let packingContent = '';
  let cashierContent = '';
  
  // These are 'let' because they might be updated by the loops below
  let menuSubtotal = 0;
  let alaCarteSubtotal = 0;
  const deliveryFee = order.delivery ? 25 : 0;

  // --- THE FIX FOR $NaN AND UPDATED EDITS ---
  // We use Number() to ensure .toFixed(2) never crashes.
  // We use the 'order' values from the DB if they exist, otherwise the defaults.
  const subtotalBeforeTax = Number(order.subtotal) || (menuSubtotal + alaCarteSubtotal + deliveryFee);
  const totalTaxes = Number(order.taxes) || 0;
  const grandTotal = Number(order.total) || (subtotalBeforeTax + totalTaxes);

  // Split the tax for the receipt (Quebec 5% / 9.975% logic)
  const gst = totalTaxes * 0.3338;
  const qst = totalTaxes * 0.6662;
  
  console.log("Final Math for Receipt:", { subtotalBeforeTax, gst, qst, grandTotal });

    packingContent += `
      <h2 style="margin:0; font-size:32px; color:#003366; text-align:center;">${order.name.toUpperCase()}</h2>
      <h3 style="margin:8px 0 16px; font-size:20px; text-align:center;">
        #${order.orderNumber} • ${order.date} • <strong>${order.delivery ? 'DELIVERY' : 'PICKUP'}</strong>
      </h3>

      <div style="text-align:center; font-weight:bold; font-size:18px; margin:12px 0; color:#c0392b;">
        PREPARED BY: ____________________________
      </div>
    `;

    if (order.delivery) {
      packingContent += `
        <p style="font-size:16px; margin:12px 0; line-height:1.5;">
          <strong>Delivery Address:</strong><br>
          ${order.streetNumber || ''} ${order.streetName || ''}${order.streetName ? ',' : ''} 
          ${order.city || ''}${order.city && order.province ? ', ' : ''}${order.province || ''} 
          ${order.postalCode ? ` ${order.postalCode}` : ''}
        </p>
      `;
    }

    packingContent += `<p style="margin:8px 0; font-size:15px;"><strong>Phone:</strong> ${order.phone}</p>`;

    if (order.menu1Persons > 0) {
      packingContent += `
        <h3 style="margin:24px 0 8px; color:#0066cc; font-size:22px;">MENU #1 (${order.menu1Persons} PPL)</h3>
        <ul style="margin:0 0 20px 0; padding-left:28px; list-style-type:disc; font-size:15px;">
      `;
      menu1Items.forEach(item => {
        const p = calculatePortion(order.menu1Persons, item);
        if (p) packingContent += `<li style="margin-bottom:5px;">${item}: ${p}</li>`;
      });
      packingContent += `</ul>`;
      menuSubtotal += 80 * order.menu1Persons;
    }

    if (order.menu2Persons > 0) {
      packingContent += `
        <h3 style="margin:24px 0 8px; color:#0066cc; font-size:22px;">MENU #2 (${order.menu2Persons} PPL)</h3>
        <ul style="margin:0 0 20px 0; padding-left:28px; list-style-type:disc; font-size:15px;">
      `;
      menu2Items.forEach(item => {
        const p = calculatePortion(order.menu2Persons, item);
        if (p) packingContent += `<li style="margin-bottom:5px;">${item}: ${p}</li>`;
      });
      packingContent += `</ul>`;
      menuSubtotal += 90 * order.menu2Persons;
    }

    if ((order.menu1Persons > 0 || order.menu2Persons > 0) && Object.keys(order.alaCarte || {}).length > 0) {
      packingContent += `<hr style="border-top:2px dotted #666; margin:24px 0;" />`;
    }

    if (Object.keys(order.alaCarte || {}).length > 0) {
      packingContent += `
        <h3 style="margin:20px 0 8px; font-size:20px;">A-LA-CARTE ITEMS</h3>
        <ul style="margin:0 0 20px 0; padding-left:28px; list-style-type:disc; font-size:15px;">
      `;
      Object.entries(order.alaCarte).forEach(([name, { qty, chosenUnit }]) => {
        packingContent += `<li style="margin-bottom:5px;">${name}: ${qty} ${chosenUnit}</li>`;
      });
      packingContent += `</ul>`;
    }

    packingContent += `<hr style="border-top:2px dotted #666; margin:24px 0;" />`;
// 1. First, handle the Notes/Requirements section for packingContent
    const notesText = order.notes || order.specialNotes || order.specialInstructions || order.requirements || '';
    if (notesText) {
      packingContent += `
        <h3 style="margin:20px 0 8px; color:#c0392b; font-size:20px;">NOTES / REQUIREMENTS</h3>
        <div style="background:#fff3cd; padding:16px; border:1px solid #ffeeba; border-radius:6px; white-space:pre-wrap; font-size:15px; line-height:1.5;">
          ${notesText}
        </div>
      `;
    }

    // 2. JavaScript Logic (Math & Payment Status)
    const hasCard = order.ccNumber && order.ccNumber.trim().length >= 15;
    const paymentStatus = hasCard 
      ? 'CARD ON FILE – will be billed once order is ready.' 
      : 'PAY AT COUNTER';

//   // Ensure we have numbers. If order.subtotal is missing, fallback to the calculation.
//     const subtotalBeforeTax = Number(order.subtotal) || (menuSubtotal + alaCarteSubtotal + deliveryFee);
    
//     // Calculate defaults
//     const defaultGst = (menuSubtotal + deliveryFee) * 0.05;
//     const defaultQst = (menuSubtotal + deliveryFee) * 0.09975;

//     // Use order.taxes if it exists, otherwise use the defaults we just calculated
//     const gst = order.taxes ? (Number(order.taxes) * 0.3338) : defaultGst;
//     const qst = order.taxes ? (Number(order.taxes) * 0.6662) : defaultQst;
    
//     // Grand total is either the saved total or the sum of our parts
//     const grandTotal = Number(order.total) || (subtotalBeforeTax + gst + qst);

    // 3. Build the Payment Info Box for cashierContent
    let paymentBox = `
      <div style="border: 2px solid #000; padding: 20px; margin: 25px 0; text-align: left; font-size: 15px; page-break-inside: avoid;">
        <strong>Payment Info${order.delivery ? ' (Delivery - Manual Processing Required)' : ''}:</strong><br />
    `;

    if (hasCard) {
      paymentBox += `
        Name on Card: ${order.cardName || '—'}<br />
        Credit Card Number: ${order.ccNumber || '—'}<br />
        Expiration Date: ${order.expDate || '—'}<br />
      `;
    } else {
      paymentBox += `
        ${paymentStatus}<br />
      `;
    }
    paymentBox += `</div>`;

    cashierContent += `
      <img src="${window.location.origin}/images/logo-removebg-preview.png" alt="Paradise Kosher" style="display:block; margin:0 auto 12px; max-width:140px; height:auto;" />

      <h1 style="text-align:center; font-size:30px; margin:0 0 6px; font-weight:bold;">${order.name.toUpperCase()}</h1>
      <div style="text-align:center; font-size:18px; margin:0 0 15px; font-weight:bold;">ORDER RECEIPT</div>
      <div style="text-align:center; font-size:16px; margin:0 0 20px; font-weight:bold;">
        Order #: ${order.orderNumber} • Service: <strong>${order.delivery ? 'DELIVERY' : 'PICKUP'}</strong> • Date: ${order.date || '—'}${order.time ? ` at ${order.time}` : ''}
      </div>

      <div style="display:flex; justify-content:space-between; gap:15px; margin:15px 0;">
        <div style="flex:1;">
          <strong>Customer Info:</strong><br />
          Phone: ${order.phone || '—'}<br />
          Email: ${order.email || '—'}
        </div>
        <div style="flex:1;">
          <strong>Order Details:</strong><br />
          ${order.company ? `Company: ${order.company}<br />` : ''}
        </div>
      </div>

      ${paymentBox}

      ${notesText ? `
        <div style="background:#f9f9f9; padding:12px; border:1px solid #ccc; margin:15px 0; white-space:pre-wrap;">
          <strong>Notes / Special Instructions:</strong><br />
          ${notesText}
        </div>
      ` : ''}

      <table style="width:100%; border-collapse:collapse; margin:15px 0;">
        <thead>
          <tr>
            <th style="border:1px solid #000; padding:8px; background:#f0f0f0; font-weight:bold;">Item Description</th>
            <th style="border:1px solid #000; padding:8px; background:#f0f0f0; font-weight:bold;">Unit Price</th>
            <th style="border:1px solid #000; padding:8px; background:#f0f0f0; font-weight:bold;">Qty</th>
            <th style="border:1px solid #000; padding:8px; background:#f0f0f0; font-weight:bold; text-align:right;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${order.menu1Persons > 0 ? `
            <tr>
              <td style="border:1px solid #000; padding:8px;">Menu #1 Selection</td>
              <td style="border:1px solid #000; padding:8px;">$80.00</td>
              <td style="border:1px solid #000; padding:8px;">${order.menu1Persons} people</td>
              <td style="border:1px solid #000; padding:8px; text-align:right;">$${(order.menu1Persons * 80).toFixed(2)}</td>
            </tr>
          ` : ''}
          ${order.menu2Persons > 0 ? `
            <tr>
              <td style="border:1px solid #000; padding:8px;">Menu #2 Selection</td>
              <td style="border:1px solid #000; padding:8px;">$90.00</td>
              <td style="border:1px solid #000; padding:8px;">${order.menu2Persons} people</td>
              <td style="border:1px solid #000; padding:8px; text-align:right;">$${(order.menu2Persons * 90).toFixed(2)}</td>
            </tr>
          ` : ''}
         ${Object.entries(order.alaCarte || {}).map(([name, item]) => {
    // Force numbers so math works
    const qty = Number(item.qty) || 0;
    // Look for a price saved on the item itself first!
    const unitPrice = Number(item.price || itemPrices[name.toUpperCase()]?.price || 0);
    const lineTotal = qty * unitPrice;

    return `
      <tr>
        <td style="border:1px solid #000; padding:8px;">${name}</td>
        <td style="border:1px solid #000; padding:8px;">$${unitPrice.toFixed(2)}</td>
        <td style="border:1px solid #000; padding:8px;">${qty} ${item.chosenUnit || ''}</td>
        <td style="border:1px solid #000; padding:8px; text-align:right;">$${lineTotal.toFixed(2)}</td>
      </tr>
    `;
}).join('')}

         ${order.delivery ? `
  <tr>
    <td style="border:1px solid #000; padding:8px;">Delivery Fee</td>
    <td style="border:1px solid #000; padding:8px;">$${(Number(order.deliveryFee) || 25).toFixed(2)}</td>
    <td style="border:1px solid #000; padding:8px;">1</td>
    <td style="border:1px solid #000; padding:8px; text-align:right;">$${(Number(order.deliveryFee) || 25).toFixed(2)}</td>
  </tr>
` : ''}
        </tbody>
      </table>

     <div style="margin-top:10px; text-align:right; font-size:14px; page-break-inside:avoid;">
        Subtotal: $${subtotalBeforeTax.toFixed(2)}<br />
        GST: $${gst.toFixed(2)}<br />
        QST: $${qst.toFixed(2)}<br />
        <div style="font-weight:bold; font-size:17px; margin-top:8px;">
          TOTAL: $${grandTotal.toFixed(2)}
        </div>
      </div>

      <p style="text-align:center; margin-top:30px; font-size:13px;">
        Thank you for your order!<br />
        Please present this receipt at pickup or keep for delivery confirmation.
      </p>
    `;

    win.document.write(`
      <html>
      <head><title>Order ${order.orderNumber} - ${order.name}</title>
      <style>
        body { font-family: Arial, sans-serif; margin:0; padding:0; font-size:14px; line-height:1.45; }
        @media print {
          .packing { page-break-after: always; }
          .cut { display: none; }
        }
        .packing { padding:1.5cm 1.2cm; }
        .cashier { padding:1.5cm 1.2cm; }
        h2 { font-size:32px; margin:0 0 10px; color:#003366; }
        h3 { font-size:22px; margin:16px 0 10px; }
        h4 { font-size:18px; margin:20px 0 8px; }
        ul { margin:0 0 20px; padding-left:28px; }
        li { margin-bottom:6px; }
        hr.dotted { border-top:2px dotted #666; margin:24px 0; }
      </style>
      </head>
      <body>
        <div class="packing">
          ${packingContent}
        </div>
        <div class="cashier">
          ${cashierContent}
        </div>
      </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();

    await fetch(`${baseURL}/admin/orders/${order._id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
      body: JSON.stringify({ printStatus: 'printed' })
    });
    fetchData();
  };
  
  // ── Main Dashboard ─────────────────────────────────────────────
return (
  <div className="min-vh-100 bg-light">
    {/* Modern Header - Fixed top, clean, subtle shadow */}
    <header className="bg-white shadow-sm sticky-top">
      <div className="container-fluid px-4 py-3">
        <div className="d-flex justify-content-between align-items-center">
          {/* Left: Logo + Title */}
          <div className="d-flex align-items-center gap-3">
            <img
              src={process.env.PUBLIC_URL + '/images/logo-removebg-preview.png'}
              alt="Paradise Kosher Logo"
              style={{ height: '48px', width: 'auto' }}
            />
            <h4 className="mb-0 fw-bold text-dark">Management Dashboard</h4>
          </div>

        
             <button
  className="btn btn-outline-info btn-lg"
  
   onClick={() => setActiveTab('totals')}
>
  📊 Totals Sales / Summary
</button>

        
  {/* Right: Logout */}
          <button
            className="btn btn-outline-danger px-4 py-2 fw-medium"
            onClick={() => {
              setIsAdmin(false);
              setActiveTab('all');
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </header>

    {/* Main Content */}
    <main className="container-fluid px-4 py-4" style={{ maxWidth: '1600px', margin: '0 auto' }}>
      {/* Filters - clean card */}

      <div className="bg-white rounded-3 shadow-sm p-4 mb-4 no-print">
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label fw-medium text-muted small mb-1">Search by name</label>
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder="Customer name..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>
          {/* NEW: Phone Number Search */}
    <div className="col-md-3">
      <label className="form-label fw-medium text-muted small mb-1">Search by phone</label>
      <input
        type="text"
        className="form-control form-control-lg"
        placeholder="Phone (e.g. 514...)"
        value={phoneSearch}
        onChange={(e) => setPhoneSearch(e.target.value)}
      />
    </div>
    {/* Add this inside your filter <div className="row"> */}
<div className="col-md-3">
  <label className="form-label fw-medium text-muted small mb-1">Product Search</label>
  <div className="input-group">
    <input
      type="text"
      className="form-control form-control-lg border-primary"
      placeholder="Menu1, Matza, etc."
      value={itemSearch}
      onChange={(e) => setItemSearch(e.target.value)}
    />
    {itemSearch && (
      <span className="input-group-text bg-primary text-white fw-bold">
        {displayList.length} 📦
      </span>
    )}
  </div>
</div>
          <div className="col-md-3">
            <label className="form-label fw-medium text-muted small mb-1">Filter by date</label>
            <input
              type="date"
              className="form-control form-control-lg"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-medium text-muted small mb-1"> </label>
            <button
              className="btn btn-outline-secondary btn-lg w-100"
              onClick={() => {
                setSearchName('');
                setFilterDate('');
                setFilterType('All');
                setActiveTab('all');
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Tabs + Kitchen Button - modern pills */}
      <div className="d-flex flex-wrap gap-3 mb-4 no-print align-items-center">
        <ul className="nav nav-pills flex-grow-1">
          <li className="nav-item">
            <button
              className={`nav-link px-4 py-3 fw-medium rounded-pill ${
                activeTab === 'all' ? 'active bg-primary text-white' : 'bg-white text-dark border'
              }`}
              onClick={() => { setActiveTab('all'); setFilterType('All'); }}
            >
              All Orders
              <span className="badge bg-light text-dark ms-2">{activeOrders.length}</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link px-4 py-3 fw-medium rounded-pill ${
                activeTab === 'pickup' ? 'active bg-success text-white' : 'bg-white text-dark border'
              }`}
              onClick={() => { setActiveTab('pickup'); setFilterType('Pickup'); }}
            >
              Pickups
              <span className="badge bg-light text-dark ms-2">{activePickups.length}</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link px-4 py-3 fw-medium rounded-pill ${
                activeTab === 'delivery' ? 'active bg-info text-white' : 'bg-white text-dark border'
              }`}
              onClick={() => { setActiveTab('delivery'); setFilterType('Delivery'); }}
            >
              Deliveries
              <span className="badge bg-light text-dark ms-2">{activeDeliveries.length}</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link px-4 py-3 fw-medium rounded-pill ${
                activeTab === 'cancelled' ? 'active bg-danger text-white' : 'bg-white text-dark border'
              }`}
              onClick={() => setActiveTab('cancelled')}
            >
              Cancelled
              <span className="badge bg-light text-dark ms-2">{archivedOrders.length}</span>
            </button>
          </li>
        </ul>
 
        {/* Kitchen Button - prominent */}
        <button
          className="btn btn-primary btn-lg px-5 py-3 fw-bold shadow-sm rounded-pill"
          onClick={() => setActiveTab('kitchen')}
        >
          🖨️ Kitchen Prep List ({activeOrders.length} active)
        </button>
      </div>
     


    {/* ── Content depending on activeTab ─────────────────────────────── */}

    {/* Live Orders Tabs (all / pickup / delivery) */}
    {(activeTab === 'all' || activeTab === 'pickup' || activeTab === 'delivery') && (
      <div>
        {/* Toolbar: Sort + View + Export */}
        <div className="d-flex gap-3 mb-3 justify-content-end no-print">
          <div className="btn-group">
            <button
              className={`btn btn-outline-secondary ${sortMode === 'latest' ? 'active' : ''}`}
              onClick={() => setSortMode('latest')}
            >
              Latest first
            </button>
            <button
              className={`btn btn-outline-secondary ${sortMode === 'alpha' ? 'active' : ''}`}
              onClick={() => setSortMode('alpha')}
            >
              A–Z by name
            </button>
          </div>

          <select
            className="form-select w-auto"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
          >
            <option value="cards">Card View</option>
            <option value="table">Table View</option>
          </select>

          <button className="btn btn-outline-primary" onClick={exportCurrentView}>
            Export CSV
          </button>
          <button className="btn btn-outline-success" onClick={optimizeDeliveryRoute}>
  Optimize Route
</button>

        </div>
    
        {/* Cards View */}
        {viewMode === 'cards' ? (
          <div className="row">
            {displayList.length === 0 ? (
              <div className="col-12 text-center py-5">
                <h5>No orders match the current filters</h5>
                <p>Try clearing search/date or switching tabs.</p>
              </div>
            ) : (
              displayList.map((o) => (
                <div className="col-md-6 mb-4" key={o._id}>
                  <div
                    className={`card shadow-sm border-start border-5 ${
                      o.printStatus === 'printed' ? 'border-secondary' :
                      o.printStatus === 'edited' ? 'border-warning' : 'border-danger'
                    }`}
                  >
                    <div className="card-body">
                      <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
                        <strong>{o.name} - #{o.orderNumber}</strong>
                        <span className="badge bg-secondary">
                          {o.date} | {o.delivery ? 'DELIVERY' : 'PICKUP'}
                        </span>
                      </div>

                      {o.printStatus === 'edited' && (
                        <div className="alert alert-warning py-1 small mb-2">
                          REPRINT REQUIRED – changes made
                        </div>
                      )}

                      <ul className="list-unstyled">
                        {o.menu1Persons > 0 && (
                          <li className="mb-3">
                            <strong className="text-primary">
                              MENU #1 ({o.menu1Persons} PPL) – ${80 * o.menu1Persons}
                            </strong>
                            <ul className="small ps-4">
                              {menu1Items.map((item) => {
                                const p = calculatePortion(o.menu1Persons, item);
                                return p ? <li key={item}>{item}: {p}</li> : null;
                              })}
                            </ul>
                          </li>
                        )}

                        {o.menu2Persons > 0 && (
                          <li className="mb-3">
                            <strong className="text-primary">
                              MENU #2 ({o.menu2Persons} PPL) – ${90 * o.menu2Persons}
                            </strong>
                            <ul className="small ps-4">
                              {menu2Items.map((item) => {
                                const p = calculatePortion(o.menu2Persons, item);
                                return p ? <li key={item}>{item}: {p}</li> : null;
                              })}
                            </ul>
                          </li>
                        )}

                        <li>
                         <strong>A-LA-CARTE ITEMS:</strong>
                            <ul className="small ps-4">
                              {Object.entries(o.alaCarte || {}).map(([name, item]) => (
                                <li key={name}>- {name.toLowerCase()}: {item.qty} {item.chosenUnit}</li>
                              ))}
                              {Object.keys(o.alaCarte || {}).length === 0 && <li className="text-muted">(none)</li>}
                            </ul>
                          </li>

                          <hr style={{ borderStyle: 'dotted', borderColor: '#999', margin: '12px 0' }} />

                          <li>
                            <strong>ADDONS / CUSTOM:</strong>
                            <ul className="small ps-4"><li className="text-muted">(reserved for addons)</li></ul>
                          </li>
                        </ul>

                        {(o.notes || o.specialNotes || o.specialInstructions || o.requirements || o.comments || o.customerNotes || o.instructions) && (
                          <div className="mt-3 p-2 bg-warning-subtle border border-warning rounded">
                            <strong>Notes / Requirements:</strong><br />
                            {o.notes || o.specialNotes || o.specialInstructions || o.requirements || o.comments || o.customerNotes || o.instructions}
                          </div>
                        )}


                      <div className="mt-3 d-flex gap-2 no-print">
                        <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(o)}>
                          ✏️ Edit
                        </button>
                        <button className="btn btn-sm btn-outline-success" onClick={() => handlePrint(o)}>
                          🖨️ Print
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleCancel(o._id)}>
                          🗑️ Cancel
                        </button>
                      </div>

                      <div className="mt-2 small text-muted">Phone: {o.phone || '—'}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Table View */
          <div className="table-responsive">
            <table className="table table-striped table-hover table-bordered">
              <thead className="table-dark">
                <tr>
                  {sortMode === 'alpha' && <th>Master #</th>}
                  <th>Name</th>
                  <th>Order #</th>
                  <th>Phone</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayList.length === 0 ? (
                  <tr>
                    <td colSpan={sortMode === 'alpha' ? 9 : 8} className="text-center py-5">
                      No orders match the current filters
                    </td>
                  </tr>
                ) : (
                  displayList.map((o, idx) => (
                    <tr key={o._id}>
                      {sortMode === 'alpha' && <td>{idx + 1}</td>}
                      <td>{o.name}</td>
                      <td>#{o.orderNumber}</td>
                      <td>{o.phone}</td>
                      <td>{o.date} {o.time ? `at ${o.time}` : ''}</td>
                      <td>{o.delivery ? 'Delivery' : 'Pickup'}</td>
                      <td>{o.total != null ? Number(o.total).toFixed(2) : '—'}</td>
                      <td>{o.printStatus || 'unprinted'}</td>
                      <td>
                        <button className="btn btn-sm btn-outline-success me-1" onClick={() => handlePrint(o)}>
                          🖨️ Print
                        </button>
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleEdit(o)}>
                          ✏️ Edit
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleCancel(o._id)}>
                          🗑️ Cancel
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        
      </div>
    )}
{/* Optimized Delivery Route - only shows in Deliveries tab */}
{/* The New Pro Component */}

{activeTab === 'delivery' && (optimizedDeliveries.length > 0 || optimizedStops.length > 0) && (
  <div className="mt-5 animate-fade-in">
    <div className="mt-4 p-4 bg-white border rounded-xl shadow-lg">
      <h5 className="mb-4 fw-bold text-primary flex align-items-center gap-2">
         Dispatch Control Center
      </h5>
      
      <DeliveryManifest 
        deliveries={optimizedDeliveries}
        setDeliveries={setOptimizedDeliveries} 
        optimizedStops={optimizedStops}
        setOptimizedStops={setOptimizedStops}
        optimizedRoute={optimizedRoute}
        setOptimizedRoute={setOptimizedRoute}
      />

      <div className="d-grid gap-3 mt-4">
        {/* DRIVER 1 SECTION */}
        <div className="d-flex align-items-center gap-3 p-2 bg-light rounded-lg">
          <button 
            onClick={() => publishRoute(1)} 
            className="btn btn-primary flex-grow-1 fw-bold shadow-sm"
            // REMOVED: optimizedStops requirement. Now works if you just have deliveries.
            disabled={optimizedDeliveries.length === 0}
          >
            🚀 Publish Driver 1 (Local)
          </button>
          <button 
            onClick={() => copyDriverLink(1)}
            className="btn btn-outline-dark"
            disabled={optimizedDeliveries.length === 0}
          >
            🔗 Copy Link
          </button>
        </div>

        {/* DRIVER 2 SECTION */}
        <div className="d-flex align-items-center gap-3 p-2 bg-light rounded-lg">
          <button 
            onClick={() => publishRoute(2)} 
            className="btn btn-info text-white flex-grow-1 fw-bold shadow-sm"
            disabled={optimizedDeliveries.length === 0}
          >
            🚀 Publish Driver 2 (City)
          </button>
          <button 
            onClick={() => copyDriverLink(2)}
            className="btn btn-outline-dark"
            disabled={optimizedDeliveries.length === 0}
          >
            🔗 Copy Link
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    {/* Kitchen Details View */}
    {activeTab === 'kitchen' && (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4 no-print">
          <h4>Kitchen Details ({activeOrders.length} Active Orders)</h4>
          <button className="btn btn-primary btn-lg" onClick={() => window.print()}>
            🖨️ Print Kitchen List
          </button>
        </div>
<div className="container mt-4">
  <div className="d-flex justify-content-between align-items-center mb-4">
    <h2>Kitchen Prep List</h2>
    <button 
      className={`btn ${isAlphabetical ? 'btn-primary' : 'btn-outline-primary'}`}
      onClick={() => setIsAlphabetical(!isAlphabetical)}
    >
      {isAlphabetical ? "← Back to Categories" : "🔍 Check for Duplicates (A-Z)"}
    </button>
  </div>

  {Object.keys(displayData).sort().map((cat) => {
    const categoryItems = displayData[cat];
    return (
      <div key={cat} className="mb-5">
        <h3 className="text-uppercase fw-bold border-bottom pb-2">{cat}</h3>
        <ul className="list-group list-group-flush">
          {categoryItems.map((agg) => (
            <li key={agg.itemName} className="list-group-item fs-4 py-3">
              {/* Highlight if there's more than one unit (Potential Dual Unit) */}
              {Object.keys(agg.unitsData).length > 1 && (
                <span className="badge bg-warning text-dark me-2" style={{fontSize: '0.5em'}}>DUAL</span>
              )}
              
              <strong>{agg.itemName}:</strong>

              {Object.entries(agg.unitsData).map(([unit, data]) => {
                const qtyStr = data.qtys.join(' + ');
                const label = unit === 'M' ? 'M (morceau)' : unit;
                
                return (
                  <div key={unit} className="ms-4 mt-1" style={{fontSize: '0.9em'}}>
                    <span className="text-muted">{qtyStr}</span> = 
                    <strong className="text-primary ms-2">Total {data.total} {label}</strong>
                    <span className="ms-3">○</span>
                  </div>
                );
              })}
            </li>
          ))}
        </ul>
      </div>
    );
  })}
</div>
        <div className="kitchen-print-area">
          <div className="print-header text-center mb-4">
            <h2 style={{ fontSize: '32px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
              PARADISE KOSHER – KITCHEN PREP LIST
            </h2>
            <p style={{ fontSize: '20px', margin: '0' }}>
              {filterDate ? `Date: ${filterDate}` : `Date: ${new Date().toLocaleDateString('en-CA')}`}
              {filterType !== 'All' ? ` | ${filterType} orders only` : ' | All orders'}
            </p>
          </div>

          {Object.keys(computeKitchenAggregates()).sort().map((cat) => {
  const categoryItems = computeKitchenAggregates()[cat];
  return (
    <div key={cat} className="mb-5 category-section">
      <h3 className="text-uppercase fw-bold border-bottom pb-2">{cat}</h3>
      <ul className="list-group list-group-flush">
        {categoryItems.map((agg) => (
          <li
            key={agg.itemName}
            className="list-group-item fs-4 py-3 border-bottom"
          >
            <strong>{agg.itemName}:</strong>
            
            {/* Loop through each unit found for this item (e.g., LB and M) */}
            {Object.entries(agg.unitsData).map(([unit, data], index) => {
              // 1. Format the Math string (5 + 4 + 2.5)
              const qtyStr = data.qtys
                .map((q) => (typeof q === 'string' ? q : (q % 1 === 0 ? q : q.toFixed(1))))
                .join(' + ');

              // 2. Handle Display Labels
              let displayUnit = unit;
              let suffix = "";
              
              if (unit === 'M') {
                displayUnit = 'M';
                suffix = " (M = morceau)";
              }

              // 3. Special Logic for Matza Balls
              let totalDisplay = `Total ${data.total % 1 === 0 ? data.total : data.total.toFixed(1)} ${displayUnit}${suffix}`;
              
              if (agg.itemName.toUpperCase().includes("MATZA BALLS NO SOUP")) {
                const containers = data.total / 4;
                totalDisplay = `Total ${data.total} balls or ${containers} cont.`;
              }

              return (
                <div key={unit} className={index > 0 ? "mt-2 pt-2 border-top dashed" : "mt-1"}>
                  <span className="text-muted" style={{ fontSize: '0.9em' }}>{qtyStr}</span>
                  <span className="mx-2">=</span>
                  <strong className="text-primary">{totalDisplay}</strong>
                  <span style={{ fontSize: '1.8em', marginLeft: '16px', verticalAlign: 'middle' }}>○</span>
                </div>
              );
            })}
          </li>
        ))}
      </ul>
    </div>
    
            );
          })}
        </div>
      </div>
    )}
{activeTab === 'totals' && (
  <div className="container-fluid pb-5 px-4">
    {/* --- HEADER: DATE FILTERING --- */}
    <div className="mb-4 text-center bg-white p-4 rounded shadow-sm border">
      <div className="d-flex justify-content-center gap-3 align-items-center flex-wrap">
        <label className="fw-bold text-secondary">Filter by Date:</label>
        <input
          type="date"
          className="form-control w-auto shadow-sm"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
        />
        <button
          className="btn btn-outline-danger btn-sm px-3"
          onClick={() => setFilterDate('')}
        >
          Clear Date
        </button>
      </div>
      <p className="text-muted small mt-2 mb-0">
        Current View: <strong>{summaryPeriod.toUpperCase()}</strong> (Defaults to today/current selection)
      </p>
    </div>

    {/* --- NAVIGATION: PERIOD & MAIN VIEW MODES --- */}
    <div className="row mb-5 justify-content-center g-3">
      {/* Period Selector (Day/Month/Year) */}
      <div className="col-12 d-flex justify-content-center mb-3">
        <div className="btn-group shadow-sm" role="group">
          {['day', 'month', 'year'].map((period) => (
            <button
              key={period}
              type="button"
              className={`btn px-4 ${summaryPeriod === period ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSummaryPeriod(period)}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Main Mode Toggle (Sales vs Kitchen) */}
      <div className="col-12 d-flex justify-content-center gap-4">
        <button
          className={`btn btn-lg px-5 fw-extrabold shadow-sm ${totalsViewMode === 'sales' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setTotalsViewMode('sales')}
        >
          💰 Total Sales
        </button>
        <button
          className={`btn btn-lg px-5 fw-extrabold shadow-sm ${totalsViewMode === 'kitchen' ? 'btn-success' : 'btn-outline-success'}`}
          onClick={() => setTotalsViewMode('kitchen')}
        >
          🍽️ Kitchen Totals
        </button>
      </div>
    </div>

    {/* --- SECTION 1: TOTAL SALES VIEW --- */}
    {totalsViewMode === 'sales' && (
      <div className="row g-4 animate__animated animate__fadeIn">
        {/* Total Orders Card */}
        <div className="col-md-3">
          <div className="card shadow-sm border-primary h-100">
            <div className="card-body text-center py-4">
              <h5 className="card-title text-primary text-uppercase small fw-bold">Total Orders</h5>
              <h3 className="display-6 fw-bold mb-1">
                {getFilteredSummaryOrders().length}
              </h3>
              <p className="text-muted mb-0">
                Value: ${getFilteredSummaryOrders().reduce((sum, o) => sum + Number(o.total || 0), 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Pickups Card */}
        <div className="col-md-3">
          <div className="card shadow-sm border-success h-100">
            <div className="card-body text-center py-4">
              <h5 className="card-title text-success text-uppercase small fw-bold">Pickups</h5>
              <h3 className="display-6 fw-bold mb-1">
                {getFilteredSummaryOrders().filter(o => !o.delivery).length}
              </h3>
              <p className="text-muted mb-0">
                ${getFilteredSummaryOrders()
                  .filter(o => !o.delivery)
                  .reduce((sum, o) => sum + Number(o.total || 0), 0)
                  .toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Deliveries Card */}
        <div className="col-md-3">
          <div className="card shadow-sm border-info h-100">
            <div className="card-body text-center py-4">
              <h5 className="card-title text-info text-uppercase small fw-bold">Deliveries</h5>
              <h3 className="display-6 fw-bold mb-1">
                {getFilteredSummaryOrders().filter(o => o.delivery).length}
              </h3>
              <p className="text-muted mb-0">
                ${getFilteredSummaryOrders()
                  .filter(o => o.delivery)
                  .reduce((sum, o) => sum + Number(o.total || 0), 0)
                  .toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Grand Total Card */}
        <div className="col-md-3">
          <div className="card shadow-sm border-warning h-100 bg-light">
            <div className="card-body text-center py-4">
              <h5 className="card-title text-warning text-uppercase small fw-bold">Total Revenue</h5>
              <h3 className="display-6 fw-bold mb-1 text-dark">
                ${getFilteredSummaryOrders().reduce((sum, o) => sum + Number(o.total || 0), 0).toFixed(2)}
              </h3>
              <p className="text-dark fw-medium small mb-0">Combined Summary</p>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* --- SECTION 2: KITCHEN TOTALS VIEW --- */}
    {totalsViewMode === 'kitchen' && (
      <div className="animate__animated animate__fadeIn">
        {/* Kitchen Toolbar (Toggles & Export) */}
        <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded shadow-sm border no-print">
          <div className="d-flex align-items-center gap-3">
            <h4 className="mb-0 fw-bold text-success">Kitchen Requirements</h4>
            <div className="btn-group shadow-sm">
              <button
                type="button"
                className={`btn btn-sm ${kitchenDisplayMode === 'cards' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => setKitchenDisplayMode('cards')}
              >
                🗂️ Card View
              </button>
              <button
                type="button"
                className={`btn btn-sm ${kitchenDisplayMode === 'table' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => setKitchenDisplayMode('table')}
              >
                📊 Table View
              </button>
            </div>
          </div>

          <button 
            className="btn btn-primary fw-bold px-4 shadow-sm" 
            onClick={exportTotalsCSV}
          >
            📥 Export Kitchen CSV
          </button>
        </div>
{/* Show optimized list below cards/table */}
{/* {optimizedDeliveries.length > 0 && (
  <div className="mt-5">
    <h4 className="text-center mb-3 fw-bold text-info">Optimized Delivery Route</h4>
    <ol className="list-group list-group-numbered">
      {optimizedDeliveries.map((o, idx) => (
        <li key={o._id} className="list-group-item">
          <strong>{idx + 1}. {o.name}</strong> - {o.streetNumber} {o.streetName}, {o.city} {o.postalCode}
          <br />
          <small className="text-muted">
            Phone: {o.phone} | Order #{o.orderNumber} | Total: ${Number(o.total || 0).toFixed(2)}
          </small>
          {o.specialNotes && (
            <div className="mt-1 small text-danger">
              <strong>Note:</strong> {o.specialNotes}
            </div>
          )}
        </li>
      ))}
    </ol>
  </div>
)} */}
        {/* Data Display Logic */}
        {kitchenDisplayMode === 'cards' ? (
          /* KITCHEN CARDS VIEW */
          <div className="row g-4">
            {Object.entries(computeKitchenAggregates(getFilteredSummaryOrders())).map(([category, items]) => (
              <div key={category} className="col-md-6 col-lg-4">
                <div className="card shadow-sm h-100 border-success">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">{category}</h5>
                  </div>
                  <div className="card-body p-0">
                    <ul className="list-group list-group-flush">
                      {items.map((item, idx) => (
                        <li key={idx} className="list-group-item d-flex justify-content-between align-items-center py-3 px-4">
                          <span className="fw-medium text-dark">{item.itemName}</span>
                          <span className="badge bg-success rounded-pill fs-6 px-3">
                            {item.total} {item.unit}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {items.length === 0 && (
                      <div className="p-5 text-center text-muted">
                        <p className="mb-0">No items detected for this category.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* KITCHEN TABLE VIEW */
          <div className="table-responsive shadow border rounded bg-white">
            <table className="table table-striped table-hover table-bordered mb-0">
              <thead className="table-success">
                <tr>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4">Product Name</th>
                  <th className="py-3 px-4 text-center">Total Quantity</th>
                  <th className="py-3 px-4">Unit</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(computeKitchenAggregates(getFilteredSummaryOrders())).map(([category, items]) => (
                  <React.Fragment key={category}>
                    {items.map((item, idx) => (
                      <tr key={`${category}-${idx}`}>
                        {idx === 0 && (
                          <td 
                            rowSpan={items.length} 
                            className="align-middle fw-bold bg-light text-success px-4"
                            style={{ width: '20%' }}
                          >
                            {category}
                          </td>
                        )}
                        <td className="px-4">{item.itemName}</td>
                        <td className="text-center fw-bold px-4">{item.total}</td>
                        <td className="px-4 text-muted">{item.unit}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {Object.keys(computeKitchenAggregates(getFilteredSummaryOrders())).length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center py-5 text-muted fs-5">
                      No kitchen data available for the selected dates.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )}
  </div>
)}
    {/* Cancelled Orders Tab – 100% identical design to the other tabs */}
    {activeTab === 'cancelled' && (
      <div>
        {/* Toolbar – same as live tabs */}
        <div className="d-flex gap-3 mb-3 justify-content-end no-print">
          <div className="btn-group">
            <button
              className={`btn btn-outline-secondary ${sortMode === 'latest' ? 'active' : ''}`}
              onClick={() => setSortMode('latest')}
            >
              Latest first
            </button>
            <button
              className={`btn btn-outline-secondary ${sortMode === 'alpha' ? 'active' : ''}`}
              onClick={() => setSortMode('alpha')}
            >
              A–Z by name
            </button>
          </div>

          <select
            className="form-select w-auto"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
          >
            <option value="cards">Card View</option>
            <option value="table">Table View</option>
          </select>

          <button className="btn btn-outline-primary" onClick={exportCurrentView}>
            Export CSV
          </button>
        </div>

        {/* Cards / Table – same structure */}
        {viewMode === 'cards' ? (
          <div className="row">
            {displayList.length === 0 ? (
              <div className="col-12 text-center py-5">
                <h5>No cancelled orders yet</h5>
              </div>
            ) : (
              displayList.map((o) => (
                <div className="col-md-6 mb-4" key={o._id}>
                  <div className="card shadow-sm border-start border-5 border-danger">
                    <div className="card-body">
                      <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
                        <strong>{o.name} - #{o.orderNumber}</strong>
                        <span className="badge bg-secondary">
                          {o.date} | CANCELLED
                        </span>
                      </div>

                      <ul className="list-unstyled">
                        {o.menu1Persons > 0 && (
                          <li className="mb-3">
                            <strong className="text-primary">MENU #1 ({o.menu1Persons} PPL)</strong>
                          </li>
                        )}
                        {o.menu2Persons > 0 && (
                          <li className="mb-3">
                            <strong className="text-primary">MENU #2 ({o.menu2Persons} PPL)</strong>
                          </li>
                        )}
                        <li>
                          <strong>A-LA-CARTE ITEMS:</strong>
                          <ul className="small ps-4">
                            {Object.entries(o.alaCarte || {}).map(([name, item]) => (
                              <li key={name}>- {name}: {item.qty} {item.chosenUnit}</li>
                            ))}
                            {Object.keys(o.alaCarte || {}).length === 0 && <li className="text-muted">(none)</li>}
                          </ul>
                        </li>
                      </ul>

                      <div className="mt-3 d-flex gap-2 no-print">
                        <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(o)}>
                          ✏️ Edit
                        </button>
                        <button className="btn btn-sm btn-outline-success" onClick={() => handlePrint(o)}>
                          🖨️ Print
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handlePermanentDelete(o._id)}
                        >
                          🗑️ Permanent Delete
                        </button>
                      </div>

                      <div className="mt-2 small text-muted">Phone: {o.phone || '—'}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover table-bordered">
              <thead className="table-dark">
                <tr>
                  {sortMode === 'alpha' && <th>Master #</th>}
                  <th>Name</th>
                  <th>Order #</th>
                  <th>Phone</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayList.length === 0 ? (
                  <tr>
                    <td colSpan={sortMode === 'alpha' ? 9 : 8} className="text-center py-5">
                      No cancelled orders yet
                    </td>
                  </tr>
                ) : (
                  displayList.map((o, idx) => (
                    <tr key={o._id}>
                      {sortMode === 'alpha' && <td>{idx + 1}</td>}
                      <td>{o.name}</td>
                      <td>#{o.orderNumber}</td>
                      <td>{o.phone}</td>
                      <td>{o.date}</td>
                      <td>CANCELLED</td>
                      <td>${Number(o.total || 0).toFixed(2)}</td>
                      <td>
                        <button className="btn btn-sm btn-outline-success me-1" onClick={() => handlePrint(o)}>
                          🖨️ Print
                        </button>
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleEdit(o)}>
                          ✏️ Edit
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => handlePermanentDelete(o._id)}>
                          Permanent Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}
</main> {/* ← This is the missing/important closing tag */}
    </div>
  );
}

export default AdminPanel;
// ──────────────────────────────────────────────────────────────
// END OF FILE – All 3 parts combined should now give a clean,
// uniform AdminPanel with tabs + separate Kitchen button
// ──────────────────────────────────────────────────────────────